import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  SpinRefusedError,
  SPIN_HINT_LINE,
  SPIN_HINT_TITLE,
  AudioBus,
  BiomePackManager,
  initManifestClient,
  LocalStorageProfileStorage,
  PlayerProfile,
  type SlotDemoScene,
  type SlotGambleFn,
  type SlotSpinFn,
  type SlotSettingsHost,
  type Theme,
  preloadRendererCode,
  worldMapOf,
  worldsWithMaps,
} from '@goodtime/engine';
import { createGoodtimeThemeRegistry } from './themes';
import { themeIdForBiome, themeIdForNode } from './themes/slotTheme';
import {
  BIOMES,
  isSlotNodeType,
  isSlotUnlocked,
  ROAD_THEMES,
  RUN_NAME,
  slotGemPrice,
  SLOT_UNLOCK_PRICE_GEMS,
  WHEEL_RUN_THEMES,
  WORLD_NODES,
  type BoardRunState,
  type SlotTrailState,
  type SpinSlotResponse,
  type WorldMapNode,
} from '@goodtime/shared';
import { createRoom } from './api/rooms';
import { fetchTrail, gambleSlot, pickTrailBox, spinSlot } from './api/slot';
import { SlotTrail } from './components/trail/SlotTrail';
import { loadGameArt } from './components/minigames/gameArt';
import { trailThemeInfo } from './components/trail/trailThemes';
import { useAutoReload } from './hooks/useAutoReload';
import { funnelBeacon, funnelMilestone, initSessionTime, trackReturning } from './api/funnel';
import { submitWheelCommand } from './api/wheelCommand';
import { BackToMapButton } from './components/BackToMapButton';
import { mapButtonInfo } from './components/mapButton';
import { BalancePill, type WalletAnchors } from './components/BalancePill';
import { WalletRow } from './components/WalletRow';
import { CanvasHost, type SceneKind } from './components/CanvasHost';
import { RefillBurst } from './components/RefillBurst';
import { OverlayDailyBonus, OverlaySettings, OverlayWallet } from './components/OverlayBoards';
import { SlotDailyBonus } from './components/SlotDailyBonus';
import { disablePushNotifications, enablePushNotifications, isPushEnabled } from './api/push';
import { restorePurchases } from './billing/purchase';
import { Capacitor } from '@capacitor/core';
import { SlotUnlockSheet } from './components/SlotUnlockSheet';
import { AgeGate, readAgeCheck, type AgeCheck } from './components/AgeGate';
import { AccountLayer } from './components/AccountLayer';
import { accountStatusSnapshot, noteAccountMoment, openAccountBoard } from './lib/accountEvents';
import { spinMoment } from './lib/accountOffers';
import { forgetOldPlayerAfterSignIn } from './lib/signInLink';
import { GemDropToast } from './components/GemDropToast';
import { SlotRelics } from './components/collect/SlotRelics';
import { SyncErrorBanner } from './components/SyncErrorBanner';
import { useSlotUnlockSync } from './hooks/useSlotUnlockSync';
import { useWalletSync } from './hooks/useWalletSync';
import { claimDailyBonus, claimDailyQuest } from './api/wallet';
import { readLaunchReferralCode, redeemPendingReferral } from './api/invites';
import { clearPendingJoinCode, readLaunchJoinCode } from './lib/joinInvite';
import { readLaunchRecoveryToken } from './lib/recoveryToken';
import { adoptPlayerId } from './api/players';
import { claimRecovery } from './api/recovery';
import { useProfileSync } from './hooks/useProfileSync';
import { preloadWheelItemIcons } from './preloadItemIcons';
import {
  WorldMapView,
  nodeTypeToScene,
  type TimeOfDay,
} from './components/world/WorldMapView';
import { WorldMapV2 } from './components/world/v2/WorldMapV2';
import { BiomeListScreen, iconForNode } from './components/world/v2/BiomeListScreen';
import { AvatarPickerModal } from './avatar/AvatarPickerModal';
import { usePlayerCustomAvatar } from './avatar/usePlayerCustomAvatar';
import { avatarImageUrl, getAvatarState } from './api/avatar';
import { WheelLobbyView } from './components/wheel/WheelLobbyView';
import { JoinByCodeModal } from './components/wheel/JoinByCodeModal';
import { FriendsModal } from './components/FriendsModal';
import { InviteFriendsDialog } from './components/InviteFriendsDialog';
import { ScheduleGameModal } from './components/scheduled/ScheduleGameModal';
import { ScheduledGameCard } from './components/scheduled/ScheduledGameCard';
import { getScheduledGame, type ScheduledGame } from './api/scheduledGames';
import {
  clearPendingGameCode,
  readLaunchGameCode,
} from './lib/scheduledGameLink';
import { PillButton } from './components/ui/PillButton';
import { Icon } from './components/Icon';
import { WheelLoadoutPreview } from './components/wheel/__dev__/WheelLoadoutPreview';
import { WheelRevealPreview } from './components/wheel/__dev__/WheelRevealPreview';
import { WheelSpinPreview } from './components/wheel/__dev__/WheelSpinPreview';
import { ReelRacePreview } from './components/wheel/__dev__/ReelRacePreview';
import { ReactionsPreview } from './components/wheel/__dev__/ReactionsPreview';
import { BiomeAnimationsPreview } from './components/__dev__/BiomeAnimationsPreview';
import { RefillBurstPreview } from './components/__dev__/RefillBurstPreview';
import { PixiKitPreview } from './components/__dev__/PixiKitPreview';
import { AccountPreview } from './components/__dev__/AccountPreview';
import { RoadPixiPreview } from './components/__dev__/RoadPixiPreview';
import { CastFavouritesPreview } from './components/__dev__/CastFavouritesPreview';
import { CaptureMenu } from './components/__dev__/CaptureMenu';
import { CardsGallery } from './components/__dev__/CardsGallery';
import { JourneyPreview } from './components/__dev__/JourneyPreview';
import { useServerConnection, type ConnectionStatus } from './hooks/useServerConnection';
import { useFeatures } from './features/useFeatures';
import { useSlotJourney } from './hooks/useSlotJourney';
import { SlotStory } from './components/story/SlotStory';
import { storyBridge } from './story/storyBridge';
import { storyArtSources } from './story/storyAssets';
import { outOfCoinsUntil, runFromConflict, setGamblePreference } from './api/road';
import { SlotJourneyChrome } from './components/journey/SlotJourneyChrome';
import { RealmMap } from './components/realm/RealmMap';
import { OverviewSwap } from './components/realm/OverviewSwap';
import { WorldMap } from './components/realm/WorldMap';
import { realmHome as isRealmHome } from './components/realm/realmHome';
import { overviewHome as isOverviewHome } from './components/realm/overviewHome';
import wheelHubUrl from './assets/wheel/hub.png';
import wheelOuterRimUrl from './assets/wheel/outer_rim.png';
import wheelPointerUrl from './assets/wheel/pointer.png';
import coinIconUrl from './assets/icon/coin.png';
import friendsIconUrl from './assets/icon/friends.png';
import joinIconUrl from './assets/icon/join.png';
import clockIconUrl from './assets/icon/clock.png';

/** Matches the server's starter wallet (AddWalletService), so the local
 *  placeholder shown before the first wallet sync, and offline play, start
 *  where a new account does. */
const STARTING_BALANCE = 500;
/** How long a first visit waits for the server session before opening the
 *  entry slot anyway (it then plays locally until the next slot entry). */
const ENTRY_SESSION_WAIT_MS = 2500;
/** How long the first-visit "tap SPIN" hint stays up if they don't spin. */
const SPIN_HINT_MS = 12000;
/** Delay before warming the wheel's images at boot. */
const WHEEL_WARMUP_DELAY_MS = 8000;

type AppView = 'biomeList' | 'map' | 'game' | 'cast';

// Signed in as another player before the last restart: forget the old
// player's copies before the profile reads them (lib/signInLink.ts).
forgetOldPlayerAfterSignIn();

// `?dev=fresh` — 1-click replay of the first-install sequence
// (added 2026-05-29 during the post-T2 polish pass). Wipes
// `goodtime.profile` + `goodtime.playerId` from LocalStorage, then
// redirects to root so
// the page boots clean: starter gem grant, first-visit slot at
// launch, fresh-profile chrome state. Cheaper than the
// manual "open DevTools → Application tab → clear key → reload"
// dance the prior debug loop required.
//
// Runs at module init so it fires BEFORE the {@link PlayerProfile}
// constructor reads from LocalStorage. Replace (not assign) so the
// reload doesn't re-trigger the wipe on its own redirect.
if (
  import.meta.env.DEV &&
  typeof window !== 'undefined' &&
  typeof localStorage !== 'undefined' &&
  window.location.search.includes('dev=fresh')
) {
  try {
    localStorage.removeItem('goodtime.profile');
    // Profile sync (2026-06-09) — also drop the persisted player id, so a
    // fresh replay mints a brand-new server player instead of re-adopting
    // the previous one's server-side profile backup on the boot sync.
    localStorage.removeItem('goodtime.playerId');
  } catch {
    /* private browsing / quota — silently degrade */
  }
  // Strip the query param + redirect to root. {@code location.replace}
  // (not assign) so the previous history entry isn't a perpetual
  // wipe-and-reload loop on the back button.
  window.location.replace('/');
}

// R3 capture-session mode — `?capture=true` flips on a body class
// that CSS uses to hide debug chrome (PAUSED · MANUAL MODE badge,
// dev-preview "DEV · WHEEL-SPIN" labels, dev panels, debug overlays).
// Set at module init so it applies even to the `?dev=*` early-return
// preview components below — Jon can combine flags, e.g.
// `?dev=wheel-spin&capture=true` to record the spin preview without
// the DEV label. See `docs/design/r3-capture-recipes.md` for the
// full per-shot URL list.
if (
  import.meta.env.DEV &&
  typeof window !== 'undefined' &&
  typeof document !== 'undefined' &&
  window.location.search.includes('capture=true')
) {
  document.body.classList.add('capture-mode');
  // Best-effort: prevent the iPhone screen from dimming during a
  // long capture sequence. Wake Lock landed in Safari 16.4 so this
  // covers any modern iOS device; older devices silently no-op.
  const wakeSrc = (navigator as unknown as { wakeLock?: { request: (kind: string) => Promise<unknown> } }).wakeLock;
  wakeSrc?.request?.('screen').catch(() => {
    /* iOS < 16.4 or denied permission — silent fallback */
  });

  // Triple-tap the top-left corner → return to the `?capture=menu`
  // launcher. Standalone PWA mode (Add to Home Screen) has no address
  // bar, so this is the only way back to the shot picker without
  // re-launching. Registered at module-init (not a React effect) so
  // it covers the `?dev=*` preview early-returns too. Invisible +
  // deliberate (3 taps in a 60px corner inside 600ms) so it never
  // fires mid-capture or shows in a recording.
  let cornerTaps = 0;
  let cornerTimer: ReturnType<typeof setTimeout> | undefined;
  document.addEventListener('pointerdown', (e) => {
    if (e.clientX <= 60 && e.clientY <= 60) {
      cornerTaps += 1;
      if (cornerTimer) clearTimeout(cornerTimer);
      cornerTimer = setTimeout(() => {
        cornerTaps = 0;
      }, 600);
      if (cornerTaps >= 3) {
        window.location.search = '?capture=menu';
      }
    } else {
      cornerTaps = 0;
    }
  });
}

/** The top safe-area inset in pixels (notch / status bar), read once. */
let safeTopCache: number | null = null;
function safeAreaTop(): number {
  if (safeTopCache !== null) return safeTopCache;
  try {
    const probe = document.createElement('div');
    probe.style.cssText = 'position:fixed;top:0;height:env(safe-area-inset-top, 0px);visibility:hidden;';
    document.body.appendChild(probe);
    safeTopCache = probe.getBoundingClientRect().height;
    probe.remove();
  } catch {
    safeTopCache = 0;
  }
  return safeTopCache;
}

export function App() {
  // The map on screen: the overview (world null) or a world's own map,
  // centred on `focus` (a slot) when it opens. `under` is the one it
  // replaced, kept beneath it until the new one has faded in. Declared
  // above the dev-only early returns so the hooks run in the same order.
  const [mapScreen, setMapScreen] = useState<MapScreen>(OVERVIEW);
  const [mapUnder, setMapUnder] = useState<MapScreen | null>(null);
  // The screen that replaced `under` has drawn: `under` fades out over it.
  const [mapUnderOut, setMapUnderOut] = useState(false);
  // The world the player has just come back from, so the strip opens zoomed on its scene and eases out; null on a fresh launch.
  const [stripFrom, setStripFrom] = useState<number | null>(null);
  // DEV-ONLY preview escape hatches — gated to dev builds. import.meta.env.DEV
  // is a compile-time `false` in production, so these routes are unreachable
  // AND the preview components tree-shake out of the prod bundle. ?dev=loadout
  // mounts the WheelLoadoutView with mock state, ?dev=biomes the biome art
  // harness, etc.; ?capture=menu is the R3 standalone-capture shot picker.
  if (import.meta.env.DEV && typeof window !== 'undefined') {
    const q = window.location.search;
    if (q.includes('dev=loadout')) return <WheelLoadoutPreview />;
    if (q.includes('dev=wheel-spin')) return <WheelSpinPreview />;
    if (q.includes('dev=reel-race')) return <ReelRacePreview />;
    if (q.includes('dev=reactions')) return <ReactionsPreview />;
    if (q.includes('dev=wheel-reveal')) return <WheelRevealPreview />;
    if (q.includes('dev=biomes')) return <BiomeAnimationsPreview />;
    if (q.includes('dev=refill-burst')) return <RefillBurstPreview />;
    if (q.includes('dev=pixi-kit')) return <PixiKitPreview />;
    if (q.includes('dev=account')) return <AccountPreview />;
    if (q.includes('dev=road-pixi')) return <RoadPixiPreview />;
    if (q.includes('dev=cast')) return <CastFavouritesPreview />;
    if (q.includes('dev=cards')) return <CardsGallery />;
    if (q.includes('dev=journey')) return <JourneyPreview />;
    if (q.includes('capture=menu')) return <CaptureMenu />;
  }

  const { player, session, tickCount, subscribers, status, errorMessage, refreshPlayer } =
    useServerConnection();
  // This player's feature flags (features/flags.ts), with the dev-only ?ff=
  // override. Dev builds expose the list as window.__features, and the
  // server's slots for them as window.__featureThemes, for the dev tools and
  // browser tests.
  const features = useFeatures(player);
  useEffect(() => {
    if (import.meta.env.DEV) {
      const w = window as unknown as { __features?: string[]; __featureThemes?: Record<string, string[]> | null };
      w.__features = features.list;
      w.__featureThemes = player?.featureThemes ?? null;
    }
  }, [features, player?.featureThemes]);
  // T-F1 follow-up (2026-05-31) — async startWheelRoom uses this ref
  // to wait for the player's displayName to land before creating the
  // wheel room (which triggers the auto-JOIN_ROOM that needs the
  // name). The state-based {@code player} would close over a stale
  // value inside the async closure; the ref always reads the latest.
  const playerRef = useRef(player);
  playerRef.current = player;
  const [scene, setScene] = useState<SceneKind>('slot');
  // Phase W7.1 — top-level view toggle. 'biomeList' shows the all-
  // episodes overview (T2, 2026-05-29) where the player picks which
  // biome to explore; 'map' shows the chosen biome's WorldMapV2 strip;
  // 'game' shows the canvas-hosted scene selector; 'cast' is the
  // multiplayer Wheel-of-Fortune lobby. The biome list is the canonical
  // landing surface — biome travel is mediated by routing back to this
  // screen, not by the prior in-map NextBiomePortal (T2 retires the
  // portal in favour of "back to all episodes").
  const [view, setView] = useState<AppView>('biomeList');
  // The realm map (flag realm_map) is home for flagged players: it stands in
  // for the episode list and the world maps, so a slot exits to it.
  const realmHome = isRealmHome(features.has('realm_map'), player !== null);
  // Each world's own map (flag world_maps): the realm map is the overview,
  // and a world with a map in this build opens it; any other keeps its board.
  // The overview is the traveller's map strip (flag overview_strip, with realm_map and world_maps): until the player has loaded the last
  // answer stands (overviewHome), so the old overview never flashes before it.
  const stripOn =
    realmHome &&
    isOverviewHome(features.has('overview_strip') && features.has('world_maps'), player !== null);
  const worldMapsOn = realmHome && (features.has('world_maps') || stripOn);
  // Apply a deployed update by reloading the web app, but ONLY from a lobby view
  // (biomeList / map) — never mid-game ('game' = slot, 'cast' = wheel room).
  useAutoReload(view === 'biomeList' || view === 'map');
  const [worldCurrentNodeId, setWorldCurrentNodeId] = useState<number>(1);
  const [worldCompleted, setWorldCompleted] = useState<ReadonlySet<number>>(
    () => new Set(),
  );
  // V1-fallback only — V2 map ignores this. Setter retired with the
  // legacy day/night nav toggle.
  const [worldTimeOfDay] = useState<TimeOfDay>('day');
  // V2 horizontal-scroll map — biome-by-biome, 3-layer parallax.
  // Default biome = 0 (kingdom) so the first scene shows the
  // commissioned panoramas; player can travel forward via the
  // NextBiomePortal on the right edge.
  const [currentBiomeIdx, setCurrentBiomeIdx] = useState<number>(0);
  // Toggle to fall back to V1 if needed during iteration.
  const useWorldMapV2 = true;

  // U4 — when a wheel node is entered, kick off a real wheel room on the
  // server and stash its UUID. WheelLobbyView consumes it via roomId
  // and the SSE consumer drives the cast strip live. Null = mock mode
  // (the standalone 🎭 Cast pill in the dev nav).
  const [wheelRoomId, setWheelRoomId] = useState<string | null>(null);
  const [wheelRoomError, setWheelRoomError] = useState<string | null>(null);
  const [wheelRoomName, setWheelRoomName] = useState<string | null>(null);
  // Social MVP — the shareable invite code for the current wheel room
  // (e.g. "GTC-4F2A"), captured from the create response. Null until the
  // room is created. Threaded down to the InviteAvatarButton so the host
  // can copy + share it; a friend who joins by code already has it.
  const [wheelRoomInviteCode, setWheelRoomInviteCode] = useState<string | null>(null);
  // F1 — audience mode. When 'audience', WheelLobbyView mounts WITHOUT
  // a playerId, so the JOIN_ROOM auto-fire is skipped and the slot
  // scene's spinFn is null (no spin button). The user spectates the
  // live match via SSE; promoting to a seat fires JOIN_ROOM via the
  // audience-mode "Take a seat" CTA in the lobby.
  const [wheelRoomMode, setWheelRoomMode] = useState<'player' | 'audience'>('player');
  // Snapshotted at startWheelRoom time so the lobby panorama + embedded
  // reel-race art match the biome the player entered from. Without this
  // the lobby always rendered the forest theme (the registry default).
  const [wheelRoomThemeId, setWheelRoomThemeId] = useState<string | null>(null);
  // Same idea for canvas-hosted scenes (slot / bingo / scratch / etc.):
  // snapshot the entered node's biome theme so the scene art matches.
  const [canvasSceneThemeId, setCanvasSceneThemeId] = useState<string | null>(null);

  /** Flip into the cast/lobby view and create a fresh wheel room.
   *  Shared between map-node entry ({@link enterWheelRoom}) and the
   *  HUD's main-game crown button ({@link handleOpenMainGame}). */
  const startWheelRoom = (
    themeId: string,
    mode: 'player' | 'audience',
    displayName: string,
    isPrivate = true,
  ): void => {
    setView('cast');
    setWheelRoomMode(mode);
    setWheelRoomError(null);
    setWheelRoomId(null);
    setWheelRoomInviteCode(null);
    setWheelRoomName(displayName);
    setWheelRoomThemeId(themeId);
    void (async () => {
      try {
        // T-F1 follow-up (2026-05-31) — wait for the player object
        // (and specifically its displayName) before creating the
        // room. WheelLobbyView's auto-JOIN fires the moment roomId
        // is set; if displayName isn't resolved yet, the server seats
        // the player as "Player <uuid-4>" until a later JOIN_ROOM
        // with the real name catches up. Gating creation here closes
        // the race at the source — by the time roomId is non-null,
        // displayName is guaranteed to be present.
        //
        // Audience mode skips the gate (no JOIN_ROOM happens), and a
        // 5s poll cap means a genuinely-broken auth doesn't lock the
        // UI forever — we surface a creation error instead.
        if (mode === 'player') {
          const start = Date.now();
          while (Date.now() - start < 5000) {
            if (playerRef.current?.displayName) break;
            await new Promise((resolve) => setTimeout(resolve, 50));
          }
          if (!playerRef.current?.displayName) {
            throw new Error('Player profile not ready');
          }
        }
        // Social MVP — rooms are created with a shareable invite code by
        // default. There's no public matchmaking yet (every room is
        // fresh + UUID-only), so coding every room is purely additive:
        // play solo-with-bots and ignore the code, or share it to pull
        // friends into THIS table (each join displaces a bot).
        const created = await createRoom('wheel_of_fortune', themeId, isPrivate);
        setWheelRoomId(created.id);
        setWheelRoomInviteCode(created.inviteCode ?? null);
        // Playtest funnel — confirmed entry into a multiplayer wheel room
        // (public match OR private host both come through here). Once-per-
        // device; fire-and-forget, never blocks room entry.
        funnelMilestone('room_joined', playerRef.current?.id);
      } catch (e) {
        console.error('[App] wheel room create failed', e);
        setWheelRoomError(
          e instanceof Error ? e.message : 'Failed to create wheel room',
        );
      }
    })();
  };

  /**
   * Social MVP — join a friend's private room from a typed invite code.
   * Resolves the code → roomId via the server, then drops into the cast
   * lobby with that roomId; WheelLobbyView's existing auto-JOIN_ROOM
   * effect fires unchanged (displacing a bot), so join-by-code reuses
   * the whole normal join path. The joiner already knows the code, so we
   * stash it for their own invite chip too.
   */
  const joinWheelRoomByCode = async (rawCode: string): Promise<void> => {
    const { resolveRoomByCode } = await import('./api/rooms');
    const room = await resolveRoomByCode(rawCode);
    // T-F1 follow-up (2026-06-04) — mirror the gate used by
    // {@link startWheelRoom}. Without this, an early visitor who pastes
    // a code BEFORE useServerConnection has the player's displayName
    // sets wheelRoomId immediately → WheelLobbyView mounts and its
    // auto-JOIN effect runs. The effect itself gates on playerName, so
    // it won't fire the JOIN_ROOM with a blank name (good), but it'll
    // re-fire whenever playerName lands. Belt-and-braces: wait for the
    // name here too so the visible "joining" beat is correctly framed
    // and there's no in-between "Player <uuid-4>" frame should the
    // visitor manage to take any other lobby action before the gate
    // un-pauses. 5s poll cap matches startWheelRoom — a genuinely-
    // broken anonymous-player creation surfaces a clear error here
    // rather than locking the UI silently.
    const start = Date.now();
    while (Date.now() - start < 5000) {
      if (playerRef.current?.displayName) break;
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
    if (!playerRef.current?.displayName) {
      throw new Error('Player profile not ready');
    }
    setWheelRoomMode('player');
    setWheelRoomError(null);
    setWheelRoomName('Friend’s table');
    setWheelRoomThemeId(room.themeId ?? null);
    setWheelRoomInviteCode(room.inviteCode ?? rawCode);
    setWheelRoomId(room.id);
    setView('cast');
    // Playtest funnel — confirmed entry into a friend's wheel room via invite
    // code (manual "Join with code" AND the ?join= auto-join both route here).
    // Once-per-device; fire-and-forget, never blocks the join.
    funnelMilestone('room_joined', playerRef.current?.id);
  };

  const enterWheelRoom = (node: WorldMapNode, mode: 'player' | 'audience'): void => {
    // Mark as the new current node + record completion of the previous
    // current node. Node-by-node progression — server-authoritative
    // version lands in W7.3 alongside save state.
    AudioBus.shared.play('coin:collect');
    setWorldCompleted((prev) => {
      const next = new Set(prev);
      next.add(worldCurrentNodeId);
      return next;
    });
    setWorldCurrentNodeId(node.id);
    startWheelRoom(themeIdForBiome(node.biome), mode, node.name);
  };

  /** HUD crown medallion → open the main multiplayer game. Spins up a
   *  Wheel room themed for the current biome and flips to the cast
   *  lobby. No node-tracking: the player isn't entering via a map node. */
  const handleOpenMainGame = (): void => {
    const biome = BIOMES[currentBiomeIdx];
    startWheelRoom(
      themeIdForBiome(currentBiomeIdx),
      'player',
      biome ? `${biome.name} Wheel` : 'Wheel',
    );
  };

  /** "Play with others" on a slot: open this world's live wheel table,
   *  dressed in the slot the player was just on (their per-world reel skin),
   *  so moving from solo to multiplayer feels like the same game. */
  const handlePlayWithOthers = (): void => {
    const node = WORLD_NODES.find((n) => n.id === worldCurrentNodeId);
    const biomeIdx = node ? node.biome : currentBiomeIdx;
    setCurrentBiomeIdx(biomeIdx);
    if (canvasSceneThemeId) {
      setMpSkinByBiome((prev) => ({ ...prev, [biomeIdx]: canvasSceneThemeId }));
    }
    funnelBeacon('play_with_others', { playerId: playerRef.current?.id, detail: String(worldCurrentNodeId) });
    const biome = BIOMES[biomeIdx];
    startWheelRoom(themeIdForBiome(biomeIdx), 'player', biome ? `${biome.name} Wheel` : 'Wheel');
  };

  /** Invite a friend — open the game-level referral dialogue (share a
   *  personal link to the game; both sides earn gems when the friend
   *  joins). Replaced the old "host a wheel table + share its join code"
   *  flow per producer 2026-06-10 ("a link to the game with a referral
   *  code"). The room-scoped invite still lives on the wheel seat rail. */
  const handleInviteFriend = (): void => {
    setShowFriends(false);
    setShowInviteFriends(true);
  };

  /**
   * Route a node tap into the actual scene change — extracted so the
   * T1 unlock-sheet flow can call it directly after a successful
   * purchase (skipping the unlock-check that would otherwise re-open
   * the sheet because the unlocked-slot state hasn't propagated
   * through React yet on the same tick).
   */
  const routeWorldNode = (node: WorldMapNode): void => {
    // Player journey: which map nodes each player opens (detail = node id,
    // resolved to a slot / world name in the portal). Every open, not once.
    funnelBeacon('node_opened', { playerId: playerRef.current?.id, detail: String(node.id) });
    if (node.type === 'wheel') {
      enterWheelRoom(node, 'player');
      return;
    }
    setWorldCompleted((prev) => {
      const next = new Set(prev);
      next.add(worldCurrentNodeId);
      return next;
    });
    setWorldCurrentNodeId(node.id);
    const targetScene = nodeTypeToScene(node.type);
    if (targetScene !== null) {
      setScene(targetScene as SceneKind);
      setCanvasSceneThemeId(themeIdForNode(node));
      setView('game');
    }
    // For unrouted node types (chest, daily, scratch, locked) we just
    // advance the current-node marker and stay on the map. W7.3 wires
    // these to their respective reward flows.
  };

  const handlePlayWorldNode = (node: WorldMapNode): void => {
    // T1 — slot tiles are gated by the gem-unlock economy. Non-slot
    // taps (wheel, chest, daily, etc.) skip the check and route
    // straight through.
    if (!isSlotUnlocked(node.id, unlockedSlotIds)) {
      // Gem-demand signal (producer 2026-10-06): every tap on a locked
      // slot, plus a separate step when the player can't afford it, with
      // "<node>:<gems>/<price>" so the portal can show how short they were.
      const playerId = playerRef.current?.id;
      funnelBeacon('locked_slot_tapped', { playerId, detail: String(node.id) });
      const price = slotGemPrice(node.id);
      if (price !== null && price > 0 && profile.gems < price) {
        funnelBeacon('gems_short', { playerId, detail: `${node.id}:${profile.gems}/${price}` });
      }
      unlockOfferRef.current = false;
      setUnlockOffer(false);
      setPendingUnlockNode(node);
      return;
    }
    routeWorldNode(node);
  };

  /** F1 — promote audience → player from inside the lobby. The lobby
   *  flips its own playerId to non-null on the next render and
   *  WheelLobbyView's auto-JOIN effect fires, putting the user in a
   *  seat without re-creating the room. */
  const handleTakeSeat = (): void => {
    setWheelRoomMode('player');
  };

  /** Universal exit-to-map handler for the back button overlay. Player-
   *  mode exits release the seat server-side via LEAVE_ROOM so the room
   *  doesn't carry a ghost PLAYER; audience exits don't need a command
   *  (closing the SSE on unmount is enough). The single-player canvas
   *  has no server-side room, so the cast-only branch is skipped.
   *
   *  <p>Slot exits route back to the biome strip ({@code 'map'}) so the
   *  player lands next to the slot they just left — they can re-enter
   *  with one tap or swipe along the row to try a sibling slot
   *  (producer brief 2026-06-06: "back from a slot should take the
   *  user to the biome, not the episodes list"). Wheel + cast exits
   *  keep the original 2026-05-29 behaviour of routing to the all-
   *  episodes list because the multiplayer Wheel is a venue, not a
   *  position on the snake path. */
  const handleExitToMap = (): void => {
    if (view === 'cast' && wheelRoomId && wheelRoomMode === 'player' && player?.id) {
      void submitWheelCommand(wheelRoomId, 'LEAVE_ROOM', player.id).catch((e) => {
        console.warn('[App] LEAVE_ROOM failed', e);
      });
    }
    const wasSlot = view === 'game' && scene === 'slot';
    setWheelRoomId(null);
    setWheelRoomError(null);
    setWheelRoomName(null);
    // The player's reel-skin choice is intentionally NOT reset here: it
    // is a persisted preference (see mpSkinThemeId below) that should
    // survive leaving and rejoining a Reel Race.
    setView(wasSlot && !realmHome ? 'map' : 'biomeList');
    if (wasSlot && realmHome) {
      // Back from a slot lands on its world's map, centred on it; a world
      // without one (or with world_maps off) lands on the realm map.
      const node = WORLD_NODES.find((n) => n.id === worldCurrentNodeId);
      const world = node && worldMapsOn && worldMapOf(node.biome) ? node.biome : null;
      setMapUnder(null);
      setStripFrom(null);
      setMapScreen((prev) => ({ world, focus: node?.id ?? null, key: prev.key + 1 }));
    }
    // Leaving a slot is when players drift off: offer them a new one.
    if (wasSlot) window.setTimeout(() => offerSlotRef.current(), 450);
  };

  // Phase D.1 — when an active server session exists, the slot scene
  // runs in server-authoritative mode. Memoised on the session id so
  // CanvasHost doesn't remount the scene on every render. Null until
  // the session is established → CanvasHost falls back to local
  // {@code RunSpinLogic} for the slot scene (offline / pre-server play
  // still works).
  // Theme of the slot on screen, read at spin time so the spin function's
  // identity doesn't change with it (the server moves that slot's Sweet
  // Trail, if it has one).
  const slotThemeRef = useRef<string | null>(null);
  // Royal Road: a spin refused because a Lamp Run is open shows that run.
  const journeyResumeRef = useRef<((run: BoardRunState) => void) | null>(null);
  // The server runs the open slot in the other mode (set below).
  const slotModeWrongRef = useRef<() => void>(() => undefined);
  // Whether the open slot is drawn as a road (set below).
  const roadModeRef = useRef<boolean>(false);
  const journeyBonusRef = useRef<boolean>(false);
  const showSettingsRef = useRef<boolean>(false);
  const slotSpinFn = useMemo<SlotSpinFn | null>(() => {
    if (!session) return null;
    const sessionId = session.id;
    return (bet) => {
      // Playtest funnel — "they actually played a slot". Fire-and-forget;
      // funnelMilestone's once-per-device guard means only the FIRST real
      // spin beacons. Must never block or throw into the spin.
      funnelMilestone('first_spin', player?.id);
      return spinSlot(sessionId, { bet, themeId: slotThemeRef.current ?? undefined }).catch((err: unknown) => {
        const run = runFromConflict(err);
        // A run open on a slot drawn as the Sweet Trail: the road was
        // switched on there; the slot remounts as a road and resumes it.
        if (run && !roadModeRef.current) slotModeWrongRef.current();
        else if (run) journeyResumeRef.current?.(run);
        const refillAt = outOfCoinsUntil(err);
        if (refillAt !== null) throw new SpinRefusedError('OUT_OF_COINS', refillAt || null);
        throw err;
      });
    };
  }, [session?.id, player?.id]);
  // Double-or-nothing on a server spin's win, decided by the server.
  const slotGambleFn = useMemo<SlotGambleFn | null>(() => {
    if (!session) return null;
    const sessionId = session.id;
    return (req) => gambleSlot(sessionId, { ...req, themeId: slotThemeRef.current ?? undefined });
  }, [session?.id]);

  // Theme registry persists for the app's lifetime. The legacy theme
  // switcher is gone; theme is now driven by biome/wheel-room context.
  const registry = useMemo(() => createGoodtimeThemeRegistry(), []);
  const [currentThemeId] = useState<string>(registry.current().id);
  const currentTheme: Theme = registry.get(currentThemeId) ?? registry.current();

  // Multiplayer reel-skin override (2026-06-06). A purely-cosmetic,
  // local theme the player picks from their unlocked slots while in a
  // Reel Race — swaps which theme's art their own slot scene renders
  // (maths + other players unaffected; themes share the engine slot-id
  // schema). Null = use the room's own theme.
  //
  // 2026-06-07 (producer): the choice persists PER BIOME. The selector
  // only offers the current biome's unlocked skins, so the applied skin
  // must be a current-biome theme too — a skin picked in one biome must
  // NOT leak into another. Stored as a { biomeIdx: themeId } map in
  // localStorage (supersedes the earlier single global key).
  const [mpSkinByBiome, setMpSkinByBiome] = useState<Record<number, string>>(
    () => {
      try {
        const raw = window.localStorage.getItem('goodtime.mpReelSkinByBiome');
        return raw ? (JSON.parse(raw) as Record<number, string>) : {};
      } catch {
        return {};
      }
    },
  );
  // Warm the multiplayer-wheel item icons into the browser cache once,
  // early, so the deploy + loadout strips don't flash their 3-letter
  // fallback while the textures download on first entry to the wheel game
  // (producer 2026-06-10). Idle-deferred inside the helper.
  useEffect(() => {
    // Delayed like the wheel warm-up below so it doesn't compete with a
    // first visit's opening slot.
    const t = setTimeout(preloadWheelItemIcons, WHEEL_WARMUP_DELAY_MS);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    try {
      window.localStorage.setItem(
        'goodtime.mpReelSkinByBiome',
        JSON.stringify(mpSkinByBiome),
      );
    } catch {
      // Storage disabled (private mode): keep the in-memory choice only.
    }
  }, [mpSkinByBiome]);
  // Active skin override for the CURRENT biome (null = use the room /
  // app default theme). Reading from the map keys the applied skin to
  // the biome, so switching biomes shows that biome's remembered skin
  // and never another biome's.
  const mpSkinThemeId = mpSkinByBiome[currentBiomeIdx] ?? null;
  const setMpSkinThemeId = (themeId: string | null): void => {
    setMpSkinByBiome((prev) => {
      const next = { ...prev };
      if (themeId) next[currentBiomeIdx] = themeId;
      else delete next[currentBiomeIdx];
      return next;
    });
  };

  // Phase G.1+G.2+G.3+I: single PlayerProfile threads through every scene
  // (wallet) and persists to localStorage (balance, streak, XP, gems,
  // theme unlocks all survive restarts). Created once via useMemo.
  const profile = useMemo(() => {
    const storage = new LocalStorageProfileStorage();
    return new PlayerProfile({ initialBalance: STARTING_BALANCE, storage });
  }, []);
  // Profile sync (2026-06-09) — capture whether localStorage already held a
  // profile at boot, BEFORE any mutation persists one. False here means a
  // fresh install or a post-reinstall wipe; the boot sync uses it to decide
  // adopt-from-server (reinstall recovery) vs push-local (single-device
  // authority). The useState initializer runs exactly once.
  const [hadStoredProfileAtBoot] = useState<boolean>(
    () =>
      typeof localStorage !== 'undefined' &&
      localStorage.getItem('goodtime.profile') !== null,
  );
  // True until onboarding completes (read once at boot). See the entry
  // effect for what a first visit does differently.
  const [firstVisit] = useState<boolean>(() => !profile.onboardingComplete);
  const [balance, setBalance] = useState<number>(profile.balance);
  const [streak, setStreak] = useState<number>(profile.loginStreak);
  const [level, setLevel] = useState<number>(profile.level);
  const [gems, setGems] = useState<number>(profile.gems);
  // Cold-launch wallet gate (2026-06-07). False until the very first
  // server wallet sync has resolved (or the connection has failed).
  // While false the HUD wallet pills render a neutral loading state
  // instead of the local placeholder balance, so the figures never
  // visibly jump from the placeholder (1000) to the real server account
  // on the first spin. Flipped true by the boot-sync effect below.
  const [walletReady, setWalletReady] = useState<boolean>(false);
  // T1 — track unlocked slot ids in React state so the world-map gating
  // re-renders when an unlock lands. PlayerProfile is the source of
  // truth; this state mirrors it for render correctness.
  const [unlockedSlotIds, setUnlockedSlotIds] = useState<readonly string[]>(
    () => profile.unlockedSlotIds,
  );
  // The map button over a slot: its world and the games there not opened yet.
  const mapInfo = mapButtonInfo(view === 'game' && scene === 'slot' ? worldCurrentNodeId : null, unlockedSlotIds);
  // Favourite slots (2026-06-20): mirror the favourited node-id set into
  // React so the favourites banner above the old biome list refreshes live.
  // Profile is the source of truth; this set syncs with the rest of the
  // snapshot. (The slot HUD's heart that set them is gone, October 2026.)
  const [favouriteSlotIds, setFavouriteSlotIds] = useState<readonly string[]>(
    () => profile.favouriteSlotIds,
  );

  // M1 slot mastery — React mirror of per-slot stars (0–3), keyed by
  // world-node id; drives the bronze/silver/gold medal on each slot tile.
  // Recomputed from the profile on mount + after a server restore;
  // updated live on 'slot-mastery-changed' (which fires only on a gain).
  const computeSlotStars = (): Map<number, number> => {
    const map = new Map<number, number>();
    for (const node of WORLD_NODES) {
      if (!isSlotNodeType(node.type)) continue;
      const stars = profile.slotStars(String(node.id));
      if (stars > 0) map.set(node.id, stars);
    }
    return map;
  };
  const [slotStars, setSlotStars] = useState<ReadonlyMap<number, number>>(
    () => computeSlotStars(),
  );

  // Reel-skins the player may switch their multiplayer view to — the
  // distinct themes behind the slots they've unlocked IN THE CURRENT
  // BIOME (producer 2026-06-07: the selector should only offer the
  // current biome's skins, not every biome the player has unlocked a
  // slot in). Free-by-default first-slot-per-biome included via
  // {@code isSlotUnlocked}. Deduped by theme id, since several nodes can
  // share a theme. Drives the top-left {@link SkinSelector} in the Reel Race.
  const unlockedSkins = useMemo<
    readonly { id: string; name: string; iconUrl?: string }[]
  >(() => {
    const seen = new Set<string>();
    const out: { id: string; name: string; iconUrl?: string }[] = [];
    for (const node of WORLD_NODES) {
      if (!isSlotNodeType(node.type)) continue;
      if (node.biome !== currentBiomeIdx) continue;
      if (!isSlotUnlocked(node.id, unlockedSlotIds)) continue;
      const themeId = themeIdForNode(node);
      if (seen.has(themeId)) continue;
      seen.add(themeId);
      const t = registry.get(themeId);
      if (t) out.push({ id: themeId, name: t.name, iconUrl: iconForNode(node) });
    }
    return out;
  }, [unlockedSlotIds, registry, currentBiomeIdx]);

  // Stage 2 of the install-size project — seed the BiomePackManager
  // from the player's already-unlocked slots. In Stage 2 every theme
  // ships in the install bundle, so being "unlocked" implies "ready
  // to play" (markReady is the in-memory bookkeeping). Stage 3 walks
  // the device filesystem cache here instead and only marks themes
  // whose pack has actually been downloaded.
  useEffect(() => {
    for (const slotId of unlockedSlotIds) {
      const node = WORLD_NODES.find((n) => String(n.id) === slotId);
      if (!node) continue;
      BiomePackManager.shared.markReady(themeIdForNode(node));
    }
  }, [unlockedSlotIds]);

  // Stage 3 — boot-time manifest fetch. Picks up the CDN URL the
  // build was configured for (VITE_ASSET_CDN_BASE), reads any cached
  // manifest from localStorage synchronously, and kicks off a
  // background refresh. Theme entries with {@code path} set get
  // routed through CloudFront once this resolves; until then they
  // transparently fall through to the bundled {@code src}, so the
  // app renders immediately on cold start even with a slow CDN.
  // Empty / unset env var leaves the manifest client inert (Stage 2
  // behaviour: everything from the install bundle).
  useEffect(() => {
    const cdnBase = import.meta.env.VITE_ASSET_CDN_BASE as string | undefined;
    // Stage 3 — in dev (no VITE_ASSET_CDN_BASE), hand the manifest
    // client a fallback prefix pointing at the Vite dev server's
    // source-asset root. The mobile app's Vite knows about DEV at
    // build time; the engine package (pre-compiled to dist) doesn't,
    // which is why we resolve + pass DEV from here. Production builds
    // (DEV folds to false) pass no fallback — unresolved paths stay
    // null + AssetLoader skip, which is correct strict prod behaviour.
    const fallbackPrefix = import.meta.env.DEV ? '/src/assets' : undefined;
    void initManifestClient(cdnBase ?? null, { fallbackPrefix });
  }, []);
  // T1 — node the player tapped that's still locked. Non-null = the
  // SlotUnlockSheet renders. Cleared on close or successful unlock+route.
  const [pendingUnlockNode, setPendingUnlockNode] = useState<WorldMapNode | null>(null);
  // Gem-unlock offer (producer 2026-10-12: "it's not clear that gems unlock
  // slots"). With enough gems for a locked slot, the unlock sheet opens
  // unasked: on leaving a slot, or after a spin takes the wallet past the
  // price. Once per app session, except that a new crossing offers again.
  // Never on a first visit (producer 2026-11-03): a new player meets the
  // game before anything asks for gems; the offer starts on the next.
  const [unlockOffer, setUnlockOffer] = useState<boolean>(false);
  // Mirrors unlockOffer for the sheet's callbacks (a buy calls onUnlocked
  // then onClose in the same tick, so the state would read stale).
  const unlockOfferRef = useRef<boolean>(false);
  const slotOfferShownRef = useRef<boolean>(false);
  const lastSlotSpinAtRef = useRef<number>(0);
  const offerBlockedRef = useRef<boolean>(false);
  const offerNextSlot = (): boolean => {
    if (slotOfferShownRef.current || firstVisit) return true;
    if (pendingUnlockNode !== null || offerBlockedRef.current) return false;
    const locked = WORLD_NODES.filter(
      (n) => isSlotNodeType(n.type) && !isSlotUnlocked(n.id, unlockedSlotIds) && (slotGemPrice(n.id) ?? 0) > 0,
    );
    const node = locked.find((n) => n.biome === currentBiomeIdx) ?? locked[0] ?? null;
    const price = node ? slotGemPrice(node.id) : null;
    if (!node || price === null || profile.gems < price) return true;
    slotOfferShownRef.current = true;
    funnelBeacon('slot_offer', { playerId: playerRef.current?.id, detail: `shown:${node.id}` });
    unlockOfferRef.current = true;
    setUnlockOffer(true);
    setPendingUnlockNode(node);
    return true;
  };
  const offerSlotRef = useRef<() => boolean>(offerNextSlot);
  offerSlotRef.current = offerNextSlot;
  useEffect(() => {
    const sub = profile.events.on('gems-changed', (e) => {
      if (e.before >= SLOT_UNLOCK_PRICE_GEMS || e.after < SLOT_UNLOCK_PRICE_GEMS) return;
      // Only gems won in play, not a boot sync of the wallet.
      if (Date.now() - lastSlotSpinAtRef.current > 15000) return;
      slotOfferShownRef.current = false;
      // After the win's celebration; wait out a gamble or bonus card.
      let tries = 0;
      const attempt = (): void => {
        if (!offerSlotRef.current() && ++tries < 8) window.setTimeout(attempt, 2500);
      };
      window.setTimeout(attempt, 2600);
    });
    return () => { sub.dispose(); };
  }, [profile]);
  const [levelUpFlash, setLevelUpFlash] = useState<number | null>(null);
  // Zero-balance auto-reload celebration — fires when PlayerProfile
  // top-ups a busted balance back up to ZERO_BALANCE_TOP_UP. The
  // <RefillBurst> overlay reads `active` as a non-null check; cleared
  // by its onComplete after the 4s sequence finishes. This is the
  // Goodtime Casino brand-promise moment — every paid-acquisition
  // creative leans on this beat.
  const [refillBurst, setRefillBurst] = useState<{ from: number; amount: number } | null>(null);

  // Warm the browser HTTP cache for the wheel hub + rim PNGs at app
  // startup so each WheelResolveCanvas mount's Pixi Assets.load hits
  // a cached response (zero RTT) instead of fetching from disk. The
  // scene gets destroyed/recreated on every match-cycle phase change
  // (REEL_RACE → STAKE remount), so without this warming the player
  // saw a ~500ms blank canvas on every wheel reveal.
  // Start Pixi's renderer code downloading now, alongside the first slot's
  // art, instead of after it.
  useEffect(() => {
    preloadRendererCode();
  }, []);

  // Deferred a few seconds so it doesn't compete with a first visit's
  // opening slot for bandwidth (nobody reaches the wheel that fast).
  useEffect(() => {
    const t = setTimeout(() => {
      for (const url of [wheelHubUrl, wheelOuterRimUrl, wheelPointerUrl, coinIconUrl]) {
        const img = new Image();
        img.src = url;
      }
    }, WHEEL_WARMUP_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  // Unsuspend the AudioContext on first user gesture anywhere in the
  // app. Browsers block playback until a real interaction; the slot
  // scene's keypress handler covers the canvas, but React-side modal
  // taps need their own resume so audio works for taps that happen
  // before the user ever reaches the slot. resume() is idempotent.
  useEffect(() => {
    const onGesture = (): void => {
      void AudioBus.shared.resume();
      window.removeEventListener('pointerdown', onGesture);
      window.removeEventListener('keydown', onGesture);
    };
    window.addEventListener('pointerdown', onGesture, { once: false });
    window.addEventListener('keydown', onGesture, { once: false });
    return () => {
      window.removeEventListener('pointerdown', onGesture);
      window.removeEventListener('keydown', onGesture);
    };
  }, []);

  useEffect(() => {
    const subs = [
      profile.events.on('balance-changed', (event) => setBalance(event.after)),
      profile.events.on('streak-changed', (event) => setStreak(event.after)),
      profile.events.on('gems-changed', (event) => setGems(event.after)),
      // XP can also move without a level-up: adopting the server's XP
      // corrects the local count in either direction.
      profile.events.on('xp-changed', () => setLevel(profile.level)),
      // T1 — keep the React mirror of unlocked-slot ids in sync so the
      // world-map gating updates immediately on purchase. Server sync
      // for the same event lives in the {@link useSlotUnlockSync} hook
      // below — splitting client mirror from server POST keeps each
      // concern isolated.
      profile.events.on('slot-unlocked', () => {
        setUnlockedSlotIds(profile.unlockedSlotIds);
      }),
      profile.events.on('slot-favourite-changed', () => {
        setFavouriteSlotIds(profile.favouriteSlotIds);
      }),
      // M1 slot mastery — a star was just earned on a slot. Update that
      // one entry so its tile medal lights immediately.
      profile.events.on('slot-mastery-changed', (event) => {
        setSlotStars((prev) => {
          const next = new Map(prev);
          next.set(Number(event.slotId), event.starsAfter);
          return next;
        });
      }),
      profile.events.on('level-up', (event) => {
        setLevel(event.level);
        // Over the slot, the slot celebrates it on the canvas.
        if (slotThemeRef.current !== null) return;
        setLevelUpFlash(event.level);
        // Clear the flash after the celebration so subsequent level-ups
        // get their own toast.
        setTimeout(() => setLevelUpFlash(null), 2200);
      }),
      profile.events.on('zero-balance-refill', (event) => {
        // Engine just topped up the busted balance. Trigger the
        // RefillBurst overlay — the brand-promise celebration.
        setRefillBurst({ from: event.from, amount: event.amount });
      }),
    ];
    return () => {
      for (const sub of subs) sub.dispose();
    };
  }, [profile]);

  // T1 — multiplayer wallet sync: mirror every successful slot unlock
  // to the server's wallets.unlocked_slot_ids. No-op when there's no
  // server player id (pure-solo paths). Lives outside the events
  // useEffect above so the eslint react-hooks/exhaustive-deps check
  // can statically verify its own deps.
  useSlotUnlockSync(profile, player?.id ?? null);

  // T1 polish (2026-05-29) — server → client wallet reconciliation.
  // Wheel-match gem credits (reels/wheel/podium drops) land on the
  // server-side wallet; this hook GETs the wallet on demand and
  // applies any positive gem delta via {@code profile.awardGems},
  // which fires {@code gems-changed} → {@link GemDropToast}. Trigger
  // calls live in the boot-sync effect below + the {@code onPayoutLanded}
  // callback passed to {@link WheelLobbyView}. {@code syncWallet} is
  // memoised per playerId inside the hook, so it's a stable effect dep.
  const { sync: syncWallet } = useWalletSync(player?.id ?? null, profile);

  // Viral invite — capture a referral code from the launch URL once (the
  // shared goodtime.casino/i/CODE link), then redeem it as soon as the player
  // exists. Idempotent + credits both sides; on success the wallet re-syncs so
  // the +gems toast fires. Producer 2026-06-10.
  const [launchReferral] = useState(() => readLaunchReferralCode());
  useEffect(() => {
    if (!player?.id) return;
    void redeemPendingReferral(
      launchReferral,
      player.id,
      player.displayName ?? '',
      syncWallet,
    );
  }, [player?.id, player?.displayName, launchReferral, syncWallet]);

  // Rehydrate the custom avatar from the server on launch. The approved (or
  // still-pending) custom image is server-authoritative and isn't in
  // localStorage on a fresh device, so without this a reinstall would silently
  // drop a player's custom avatar. Built-in faces are left to localStorage.
  const [, setCustomAvatar] = usePlayerCustomAvatar();
  useEffect(() => {
    const pid = player?.id;
    if (!pid) return;
    let cancelled = false;
    void getAvatarState(pid)
      .then((s) => {
        if (cancelled) return;
        if (s.activeCustomUploadId) {
          setCustomAvatar({
            uploadId: s.activeCustomUploadId,
            url: avatarImageUrl(s.activeCustomUploadId, pid),
            status: 'APPROVED',
          });
        } else if (s.pendingUploadId) {
          setCustomAvatar({
            uploadId: s.pendingUploadId,
            url: avatarImageUrl(s.pendingUploadId, pid),
            status: 'PENDING',
          });
        } else {
          setCustomAvatar(null);
        }
      })
      .catch(() => {/* non-fatal — fall back to the local/built-in avatar */});
    return () => {
      cancelled = true;
    };
  }, [player?.id, setCustomAvatar]);

  // Auto-join a friend's table from the invite link. The shared
  // goodtime.casino/r/GTC-XXXX sends a web visitor to
  // play.goodtime.casino/?join=GTC-XXXX; we capture the code once (it survives
  // the launch round-trip via localStorage, see lib/joinInvite) and, as soon as
  // the player exists, drop them straight into the table via the same path the
  // "Join with code" modal uses. Fires at most once; the code is cleared after
  // the attempt so a refresh won't re-join.
  const [pendingJoinCode] = useState(() => readLaunchJoinCode());
  const autoJoinFired = useRef(false);
  useEffect(() => {
    if (autoJoinFired.current || !pendingJoinCode || !player?.id) return;
    autoJoinFired.current = true;
    void (async () => {
      try {
        await joinWheelRoomByCode(pendingJoinCode);
      } catch (err) {
        // Stale/expired code or a transient failure — drop it; the table code is
        // still shown on the invite, so they can join manually via "Join with code".
        console.warn('[auto-join] failed for', pendingJoinCode, err);
      } finally {
        clearPendingJoinCode();
      }
    })();
  }, [pendingJoinCode, player?.id]);

  // §6.E — open from a host's "Schedule a game" link
  // (play.goodtime.casino/?game=CODE). Mirrors the ?join auto-join above:
  // we read the code once at launch (it survives a launch round-trip via
  // localStorage, see lib/scheduledGameLink), resolve it to the public
  // ScheduledGame, and render the ScheduledGameCard. Unlike ?join we don't
  // auto-enter — the friend sees the gorgeous invite card first and taps
  // "Join now" (the card owns the race-safe room create/claim). Resolving
  // doesn't need the player id, so it fires as soon as the app mounts.
  const [pendingGameCode] = useState(() => readLaunchGameCode());
  const gameInviteFired = useRef(false);
  useEffect(() => {
    if (gameInviteFired.current || !pendingGameCode) return;
    gameInviteFired.current = true;
    void (async () => {
      try {
        const game = await getScheduledGame(pendingGameCode);
        setScheduledGameInvite(game);
      } catch (err) {
        // Unknown / cancelled / expired code — drop it silently; the app
        // boots normally to the map.
        console.warn('[scheduled-game] could not resolve', pendingGameCode, err);
        clearPendingGameCode();
      }
    })();
  }, [pendingGameCode]);

  // Account recovery — arrived via an emailed magic link (?recover=TOKEN). Claim
  // it once the player exists: the server returns the ORIGINAL player id to
  // adopt; we persist it and reboot so getOrCreatePlayer + profile-sync restore
  // that account (progress). Invalid/expired links are ignored. When the token
  // resolves to the player we already are, there's nothing to adopt — the boot
  // sync below already keeps this player's profile + wallet hydrated.
  const [pendingRecoveryToken] = useState(() => readLaunchRecoveryToken());
  const recoveryFired = useRef(false);
  useEffect(() => {
    if (recoveryFired.current || !pendingRecoveryToken || !player?.id) return;
    recoveryFired.current = true;
    void (async () => {
      try {
        const { playerId, token: playerTokenValue } = await claimRecovery(pendingRecoveryToken);
        if (playerId && playerId !== player.id) {
          await adoptPlayerId(playerId, playerTokenValue);
          window.location.replace('/'); // reboot → adopt id → profile-sync restores progress
        }
      } catch (err) {
        console.warn('[recovery] claim failed (invalid or expired link)', err);
      }
    })();
  }, [pendingRecoveryToken, player?.id]);

  // Acquisition funnel (2026-06-15) — once the player id exists, fire a
  // `landing` beacon carrying BOTH the attribution token (the LP's shared
  // .goodtime.casino cookie, or an organic one) AND the player id. That single
  // row is the player↔attribution link the Stripe/RevenueCat purchase webhook
  // uses to attribute the conversion back to the ad click. Platform is tagged
  // automatically (web / ios / android). Fire-and-forget; see api/funnel.ts.
  useEffect(() => {
    if (!player?.id) return;
    funnelBeacon('landing', { playerId: player.id });
    // Playtest funnel — record day-one on first load; on a later calendar day
    // this fires the once-ever `returning` milestone. Best-effort, never throws.
    trackReturning(player.id);
  }, [player?.id]);

  // Engagement-time tracking (2026-06-16) — accumulate active on-page time and
  // beacon it as a `session` step on hide/pagehide, for the analytics portal.
  // Mount-once; the tracker reads the playerId at send time.
  useEffect(() => {
    initSessionTime();
  }, []);

  // Profile sync (2026-06-09) — durable server backup of the full profile
  // snapshot (settings, unlocks, artefacts, streaks) keyed by the persisted
  // player id, so state survives a reinstall. hydrateFromServer() runs in
  // the boot effect below (adopt-on-reinstall, else establish the backup);
  // ongoing changes debounce-push inside the hook. Currency stays
  // authoritative via the wallet sync above.
  const { hydrateFromServer } = useProfileSync(
    profile,
    player?.id ?? null,
    hadStoredProfileAtBoot,
  );

  // Cold-launch wallet hydration (2026-06-07). Fetch the server's
  // authoritative wallet as soon as the anonymous player id resolves —
  // BEFORE the player can reach a slot and spin. Previously the wallet
  // was only ever synced from the wheel's onPayoutLanded, so a fresh
  // launch showed the local placeholder balance (1000) until the first
  // spin, then snap-corrected to the real account (e.g. 500) — reading
  // as "lost ~600 coins", contradicting the "no losses" promise. The
  // first sync reconciles silently (no wager XP, no celebration toasts;
  // see useWalletSync's first-sync branch), then we snapshot the
  // reconciled profile into the React mirrors and lift the loading gate.
  useEffect(() => {
    if (!player?.id) return;
    let cancelled = false;
    void (async () => {
      // 1. Profile sync (2026-06-09) — adopt the server backup on a
      //    reinstall (local cache empty at boot), else establish/refresh it
      //    from local state. Silent; the mirror refresh below reflects it.
      //    Alongside it, a first visit claims the day-1 daily bonus on the
      //    server wallet, so it's there before the slot opens and the server
      //    keeps it (a local-only claim was taken back by the next server
      //    spin). A failed claim leaves the gift button up to retry.
      const [, bonus] = await Promise.all([
        hydrateFromServer(),
        firstVisit ? claimDailyBonus(player.id).catch(() => null) : null,
      ]);
      if (cancelled) return;
      if (bonus) profile.applyDailyBonusClaim({ ...bonus, coins: bonus.wallet.coins });
      // 2. Wallet stays authoritative for currency — reconcile coins/gems
      //    on top of whatever the profile sync restored.
      await syncWallet();
      if (cancelled) return;
      // 3. Snapshot the reconciled profile into the React mirrors + lift the
      //    loading gate. The silent setters above don't emit, so pull the
      //    authoritative values straight from the getters.
      setBalance(profile.balance);
      setGems(profile.gems);
      setLevel(profile.level);
      setStreak(profile.loginStreak);
      setUnlockedSlotIds(profile.unlockedSlotIds);
      setFavouriteSlotIds(profile.favouriteSlotIds);
      setSlotStars(computeSlotStars());
      setCanClaimDaily(profile.canClaimDailyBonus());
      setWalletReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [player?.id, hydrateFromServer, syncWallet, profile, firstVisit]);

  // Offline / connection-failure fallback — if the player could never
  // be created (server unreachable), there's no wallet to fetch, so
  // lift the loading gate and let the local profile values show. Pure-
  // solo play stays fully functional; the local placeholder IS the
  // truth when there's no server account behind it.
  useEffect(() => {
    if (status !== 'error') return;
    setWalletReady(true);
    // No server wallet to claim on: a first visit takes its day-1 bonus
    // locally, as offline play does.
    if (firstVisit && profile.canClaimDailyBonus()) profile.claimDailyBonus();
  }, [status, firstVisit, profile]);

  // DEV-ONLY: ?dev=force-refill fires the synthetic refill demo once
  // on mount so the RefillBurst overlay plays without needing to walk
  // the onboarding modal or bust the slot naturally. Useful for the
  // R3 capture session (clean video grabs of the overlay against any
  // backdrop) and one-shot brand-feel reviews.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!window.location.search.includes('dev=force-refill')) return;
    // Defer one tick so the React subscription on `zero-balance-refill`
    // is wired before the synthetic emit fires.
    const id = window.setTimeout(() => profile.triggerRefillDemo(), 50);
    return () => window.clearTimeout(id);
  }, [profile]);

  // DEV-ONLY: ?dev=unlock-all unlocks every slot in the solo profile so
  // the full catalogue is playable for local testing without grinding
  // gems. Explicitly gated on import.meta.env.DEV so the param is inert
  // in the production bundle (the soft-launch build) even if appended by
  // hand. unlockSlot(id, 0) is a free unlock that persists, so the state
  // survives reloads after the param is dropped from the URL; the React
  // mirror below refreshes the world-map gating without a reload.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (typeof window === 'undefined') return;
    if (!window.location.search.includes('dev=unlock-all')) return;
    let changed = false;
    for (const node of WORLD_NODES) {
      if (!isSlotNodeType(node.type)) continue;
      if (profile.unlockSlot(String(node.id), 0)) changed = true;
    }
    if (changed) setUnlockedSlotIds(profile.unlockedSlotIds);
  }, [profile]);

  // First visit: straight into a slot. New players used to get two tour
  // cards, the episode list, a world map and a slot preview before their
  // first spin (six taps); most left inside ten seconds. Now the first
  // visit, and any ?slot=<node id> link, opens a slot directly and the
  // tour's grants land silently (see the entry effect below).
  const [entryNode] = useState<WorldMapNode | null>(() => {
    const linkedId = new URLSearchParams(window.location.search).get('slot');
    const linked = linkedId
      ? WORLD_NODES.find((n) => String(n.id) === linkedId && isSlotNodeType(n.type))
      : undefined;
    if (linked) return linked;
    return firstVisit ? (WORLD_NODES.find((n) => isSlotNodeType(n.type)) ?? null) : null;
  });
  // True from boot until the entry slot opens; a splash covers the home
  // screen meanwhile so the player never sees it flash.
  const [entryPending, setEntryPending] = useState<boolean>(entryNode !== null);
  // Adults only: the age check covers everything until it is answered.
  const [ageCheck, setAgeCheck] = useState<AgeCheck>(readAgeCheck);
  const [entryWaitOver, setEntryWaitOver] = useState<boolean>(false);
  // First-visit slot session: bet locked at 25 for a noticeable first win
  // (UX Spec §4.1) and a "tap SPIN" hint until the first spin. On from the
  // start when a first visit opens straight into a slot: the slot builds
  // behind the entry splash, and switching it on later rebuilt the slot as
  // the splash lifted (a second load, and a spin on the old one was lost).
  const [firstSlotSession, setFirstSlotSession] = useState<boolean>(() => firstVisit && entryNode !== null);
  const [showSpinHint, setShowSpinHint] = useState<boolean>(false);
  // Daily bonus modal: on launch if eligible. A first visit claims day 1
  // inline instead (handleFirstVisitGrants), so no modal for them.
  const [showDailyBonus, setShowDailyBonus] = useState<boolean>(
    () => profile.canClaimDailyBonus() && !firstVisit,
  );
  // Eligibility flag drives the HUD's gift-button visibility — hidden
  // entirely once today's bonus has been claimed (no point pretending
  // there's something to collect when the preview reward is 0).
  const [canClaimDaily, setCanClaimDaily] = useState<boolean>(() =>
    profile.canClaimDailyBonus(),
  );
  useEffect(() => {
    const sub = profile.events.on('daily-bonus-claimed', () => {
      setCanClaimDaily(false);
    });
    return () => sub.dispose();
  }, [profile]);
  /** The daily-bonus card's CLAIM: on the server wallet, which holds the
   *  streak, so the coins outlast the next server spin. (Offline, with no
   *  player, the card claims on the local profile.) Null when there was
   *  nothing to claim (already claimed today, e.g. on another device) or
   *  the claim failed. */
  const claimDailyBonusNow = useCallback(async (): Promise<{ reward: number; streak: number } | null> => {
    const pid = player?.id;
    if (!pid) return null;
    try {
      const r = await claimDailyBonus(pid);
      profile.applyDailyBonusClaim({ ...r, coins: r.wallet.coins });
      setCanClaimDaily(profile.canClaimDailyBonus());
      return r.claimed ? { reward: r.reward, streak: r.streak } : null;
    } catch (e) {
      profile.events.emit({
        kind: 'sync-error',
        operation: 'daily-bonus',
        message: e instanceof Error ? e.message : String(e),
      });
      return null;
    }
  }, [player?.id, profile]);
  /** The world map's daily-quest claim: on the server wallet, which holds
   *  the cooldown, so the coins outlast the next server spin and the gem the
   *  next boot sync. Offline, with no player, it claims on the local
   *  profile. Null when already claimed today (e.g. on another device);
   *  rejects when the claim failed, after raising the sync banner. A second
   *  tap while a claim is in flight gets the same claim. */
  const dailyQuestClaimRef = useRef<Promise<{ coins: number; gems: number } | null> | null>(null);
  const claimDailyQuestNow = useCallback((): Promise<{ coins: number; gems: number } | null> => {
    const pid = player?.id;
    if (!pid) return Promise.resolve(profile.claimDailyQuest());
    if (dailyQuestClaimRef.current) return dailyQuestClaimRef.current;
    const claim = (async () => {
      try {
        const r = await claimDailyQuest(pid);
        profile.applyDailyQuestClaim({
          ...r,
          walletCoins: r.wallet.coins,
          walletGems: r.wallet.gems,
        });
        return r.claimed ? { coins: r.coins, gems: r.gems } : null;
      } catch (e) {
        profile.events.emit({
          kind: 'sync-error',
          operation: 'daily-quest',
          message: e instanceof Error ? e.message : String(e),
        });
        throw e;
      } finally {
        dailyQuestClaimRef.current = null;
      }
    })();
    dailyQuestClaimRef.current = claim;
    return claim;
  }, [player?.id, profile]);
  // Contextual explainer — tapping the gem chip opens a sheet that
  // explains what gems are for + the coin auto-reload promise (replaces
  // the legacy gems→coins/IAP shop modal, which no longer matched the
  // T1 economy + infinite-coin model and has been removed).
  const [showGemInfo, setShowGemInfo] = useState<boolean>(false);
  // Avatar picker — opened by tapping the avatar marker on the world map.
  const [showAvatarPicker, setShowAvatarPicker] = useState<boolean>(false);
  // Social MVP — the "join a friend's table by code" modal.
  const [showJoinByCode, setShowJoinByCode] = useState<boolean>(false);
  const [showFriends, setShowFriends] = useState<boolean>(false);
  const [showInviteFriends, setShowInviteFriends] = useState<boolean>(false);
  // §6.E — "Schedule a game with friends" host modal.
  const [showScheduleGame, setShowScheduleGame] = useState<boolean>(false);
  // §6.E — an invited friend's scheduled-game card, resolved from a
  // ?game=CODE launch link. Null until the code resolves (or stays null
  // when there's no link / the code is unknown).
  const [scheduledGameInvite, setScheduledGameInvite] =
    useState<ScheduledGame | null>(null);
  // Phase 4 settings cog — opens audio prefs modal. Triggered by the
  // gear button in the slot HUD.
  const [showSettings, setShowSettings] = useState<boolean>(false);

  // Position bridge — live coin/gem chip centres reported by the persistent
  // BalancePill, fed to the slot scene so its take-win / gem-drop land on the
  // real wallet chips instead of a hardcoded offset that drifts whenever the
  // pill's layout changes (e.g. the in-slot streak-chip removal, 2026-06-10).
  const [walletAnchors, setWalletAnchors] = useState<WalletAnchors | null>(
    null,
  );

  // Whether the current game scene has finished loading and is on screen
  // (CanvasHost's loading card is down). CanvasHost says so itself: a
  // slot opened by link can finish loading before the view switches to
  // the game, and the same scene then stays up, so resetting on a view
  // change left the slot's chrome (the trail, Play with others) hidden.
  const [gameSceneShown, setGameSceneShown] = useState<boolean>(false);
  // The slot's name flashes on its canvas each time a slot opens, once
  // it's actually drawn, not over the loading card.
  const handleGameSceneShown = useCallback((shown: boolean): void => {
    setGameSceneShown(shown);
    if (shown && scene === 'slot') void slotSceneRef.current?.playNameFlash();
  }, [scene]);

  // ---- Sweet Trail (single-player side game, producer 2026-10-06) ----
  // Server-authoritative board beside the reels on slots that have one.
  // Loaded when a slot opens; replaced by the trail on each spin response,
  // applied when the reels land so the token moves with the result.
  const slotThemeId = view === 'game' && scene === 'slot' ? canvasSceneThemeId : null;
  slotThemeRef.current = slotThemeId;
  // Royal Road replaces the Sweet Trail on road slots for flagged players:
  // slots this build can draw a road on, where the server's list for the
  // player has the road on too. The Wheel Run does the same on the other
  // slots, by its own list (`wheel_run`); the Sweet Trail stays for any slot
  // the server doesn't list.
  const roadOn = useCallback(
    (themeId: string) =>
      (ROAD_THEMES.includes(themeId) && features.onSlot('royal_road', themeId)) ||
      (WHEEL_RUN_THEMES.includes(themeId) && features.onSlot('wheel_run', themeId)),
    [features],
  );
  // A slot just switched to a road (or a Wheel Run) keeps its Sweet Trail
  // while a bonus there waits: the server converts the row once it's
  // played, so it's played on the trail first (plan section 8).
  const [trailFirst, setTrailFirst] = useState<string | null>(null);
  const trailFirstRef = useRef<string | null>(null);
  trailFirstRef.current = trailFirst;
  const roadMode = slotThemeId !== null && roadOn(slotThemeId) && trailFirst !== slotThemeId;
  roadModeRef.current = roadMode;
  // It holds only while that slot is open.
  useEffect(() => {
    if (trailFirstRef.current && trailFirstRef.current !== slotThemeId) setTrailFirst(null);
  }, [slotThemeId]);
  // A world can be switched on or off on the server while the app is open.
  // Fetch the player's slots again as a slot opens, and when a spin answers
  // in the other mode (a road on a slot drawn as the Sweet Trail, or the
  // other way round); the slot then remounts in the server's mode (CanvasHost
  // rebuilds the scene when `road` changes). From spins, at most every 30 s
  // while the slot stays in one mode, in case the server runs a road this
  // build can't draw.
  const slotModeCheckedAt = useRef(0);
  useEffect(() => {
    slotModeCheckedAt.current = 0;
    if (slotThemeId) refreshPlayer();
  }, [slotThemeId, refreshPlayer]);
  useEffect(() => {
    slotModeCheckedAt.current = 0;
  }, [roadMode]);
  slotModeWrongRef.current = () => {
    const now = Date.now();
    if (now - slotModeCheckedAt.current < 30_000) return;
    slotModeCheckedAt.current = now;
    refreshPlayer();
  };
  const journey = useSlotJourney({
    sessionId: session?.id ?? null,
    playerId: player?.id ?? null,
    themeId: slotThemeId,
    enabled: roadMode,
    getScene: () => slotSceneRef.current,
    isBlocked: () => showSettingsRef.current || journeyBonusRef.current,
    syncWallet,
    profile,
    onNoRoad: (themeId) => {
      const sid = session?.id;
      if (!sid) return;
      void fetchTrail(sid, themeId).then((t) => {
        if (t && slotThemeRef.current === themeId && sweetTrailBonusWaits(t)) setTrailFirst(themeId);
      });
    },
  });
  const journeyOnSpinLanded = journey.onSpinLanded;
  journeyResumeRef.current = journey.resumeRun;
  const refreshRoad = journey.refreshRoad;
  // The server needs the Settings gamble choice (Hi-Lo bonuses and Sir
  // Clank's lamp follow it): sent when it changes, and once per player when
  // it's off. The road comes again after, for the lamp's game.
  useEffect(() => {
    const id = player?.id;
    if (!id) return;
    const send = (on: boolean) =>
      void setGamblePreference(id, on).then(refreshRoad, () => {});
    if (!profile.gambleEnabled) send(false);
    const sub = profile.events.on('gamble-pref-changed', (e) => send(e.after));
    return () => sub.dispose();
  }, [player?.id, profile, refreshRoad]);
  journeyBonusRef.current = journey.bonus !== null;
  showSettingsRef.current = showSettings;
  const [trail, setTrail] = useState<SlotTrailState | null>(null);
  const [trailSpinKey, setTrailSpinKey] = useState<number>(0);
  // A winning spin's celebration plays first; the trail waits for it.
  const [trailRevealDelay, setTrailRevealDelay] = useState<number>(0);
  const [showTrailBonus, setShowTrailBonus] = useState<boolean>(false);
  const slotSceneRef = useRef<SlotDemoScene | null>(null);
  // Only server spins move the trail, so the panel hides while a slot plays
  // locally (a first visit that opened before the session was ready) and
  // shows once that slot switches to server spins.
  const [slotServerMode, setSlotServerMode] = useState<boolean>(false);
  // The gamble overlay, and any bonus or pop-up drawn on the slot's canvas
  // (hold and spin), cover the slot: DOM chrome over it (the trail, Play
  // with others) steps aside while either is open.
  const [canvasCover, setCanvasCover] = useState<{ gamble: boolean; popup: boolean }>({ gamble: false, popup: false });
  const gambleOpen = canvasCover.gamble;
  const canvasPopup = canvasCover.popup;
  // The slot offer waits while a gamble or the trail bonus is on screen.
  offerBlockedRef.current = gambleOpen || showTrailBonus || canvasPopup;
  useEffect(() => {
    setTrail(null);
    setShowTrailBonus(false);
    if (!session || !slotThemeId || roadMode) return;
    let cancelled = false;
    void fetchTrail(session.id, slotThemeId).then((t) => {
      if (cancelled) return;
      setTrail(t);
      // The trail's game was left open (a reload): it opens again. So does
      // a bonus that must be played before the slot's road can start; with
      // none left (played in another tab), the road starts.
      const first = trailFirstRef.current === slotThemeId;
      if (first && !(t && sweetTrailBonusWaits(t))) setTrailFirst(null);
      else if (t?.game || first) setShowTrailBonus(true);
    });
    return () => {
      cancelled = true;
    };
  }, [session?.id, slotThemeId, roadMode]);
  const handleSlotSpinLanded = useCallback(
    (response: SpinSlotResponse): void => {
      lastSlotSpinAtRef.current = Date.now();
      const moment = spinMoment(response);
      if (moment) noteAccountMoment(moment);
      // The server counts the bet as XP and credits any level-up gems to
      // the wallet. Adopt its XP (the local count fired the level-up already
      // if it saw the same crossing) and mirror the gems, as the trail's gem
      // squares are below. Also marks the server as the gem source in case
      // this spin beat the boot wallet sync.
      if (typeof response.wallet.xp === 'number') {
        profile.setServerWallet(true);
        profile.reconcileXp(response.wallet.xp, true);
      }
      if (response.levelUp) profile.awardGems(response.levelUp.gems);
      // The server topped up an empty wallet to pay for this spin: show the
      // refill, counting up from what the player had before it.
      if (response.refill && response.refill > 0) {
        const before = response.wallet.coins - response.refill - response.settlement.net;
        setRefillBurst({ from: Math.max(0, before), amount: response.refill });
      }
      // The server ran this spin in the other mode: see above.
      if (response.road ? !roadModeRef.current : response.trail && roadModeRef.current) {
        slotModeWrongRef.current();
      }
      // Royal Road: gem squares were credited server-side; mirror them.
      const roadGems = journeyOnSpinLanded(response);
      if (roadGems > 0) profile.awardGems(roadGems);
      storyBridge.onSpinLanded(response);
      const t = response.trail;
      if (!t) return;
      setTrail(t);
      // Big wins (10x the bet and up) get the longer celebration.
      const won = response.settlement.totalWinnings;
      setTrailRevealDelay(won >= 10 * response.settlement.bet ? 3200 : won > 0 ? 1500 : 0);
      setTrailSpinKey((k) => k + 1);
      // Gem squares were credited server-side; mirror them on the local
      // profile so the wallet pill and gem toast update now.
      const gems = t.steps.filter((s) => s.kind === 'GEMS').reduce((n, s) => n + s.amount, 0);
      if (gems > 0) profile.awardGems(gems);
      if (t.steps.some((s) => s.kind === 'BONUS')) {
        funnelBeacon('trail_bonus', { playerId: playerRef.current?.id, detail: `${t.themeId}:${t.level}` });
        window.setTimeout(() => setShowTrailBonus(true), 1100);
      }
    },
    [profile, journeyOnSpinLanded],
  );
  const handleTrailPick = useCallback(
    async (index: number): Promise<{ trail: SlotTrailState; coins: number | null }> => {
      if (!session || !trail) throw new Error('no trail');
      const res = await pickTrailBox(session.id, trail.themeId, index);
      const picked = res.trail.bonus?.picks.find((p) => p.index === index);
      if (picked?.kind === 'GEMS') profile.awardGems(picked.amount);
      setTrail(res.trail);
      // Coins land on the server wallet: the bonus flies them into the
      // slot's counter, then shows the server's figure.
      return { trail: res.trail, coins: res.wallet.coins };
    },
    [session?.id, trail, profile],
  );

  // Bind the player's persisted music + SFX prefs to the AudioBus
  // singleton. Initial: read from profile on mount and apply. On change:
  // listen for the profile event and apply. The settings modal writes to
  // the profile; this hook applies the result everywhere audio plays.
  useEffect(() => {
    // Master mute retired with the Settings "Sound" toggle — audio is now
    // governed solely by the Music + Sound-effects channels, so the master
    // stays unmuted (this also frees any user previously stranded muted by
    // the old master toggle).
    AudioBus.shared.setMuted(false);
    AudioBus.shared.setMusicEnabled(profile.musicEnabled);
    AudioBus.shared.setSfxEnabled(profile.sfxEnabled);
    const subs = [
      profile.events.on('music-pref-changed', (e) => {
        AudioBus.shared.setMusicEnabled(e.after);
      }),
      profile.events.on('sfx-pref-changed', (e) => {
        AudioBus.shared.setSfxEnabled(e.after);
      }),
    ];
    return () => { for (const s of subs) s.dispose(); };
  }, [profile]);

  /** What the old tour granted, without the tour: the 100 starter gems and
   *  the onboarding-complete flag. The day-1 daily bonus it also granted
   *  is claimed at boot instead (coins land, streak starts at day 1), with
   *  no modal. Idempotent. */
  const handleFirstVisitGrants = (): void => {
    profile.claimStarterGems();
    profile.markOnboardingComplete();
    // The day-1 daily bonus is claimed by the boot wallet sync (on the
    // server) or, offline, by the connection-failure fallback.
    setShowDailyBonus(false);
  };

  // Open the entry slot once the server session exists, so the first spins
  // are server-authoritative. If the session is slow, open anyway after a
  // short wait: the slot plays locally until the session arrives, then
  // switches to server spins between spins (never mid-bonus).
  useEffect(() => {
    if (!entryPending) return;
    const t = setTimeout(() => setEntryWaitOver(true), ENTRY_SESSION_WAIT_MS);
    return () => clearTimeout(t);
  }, [entryPending]);
  useEffect(() => {
    if (!entryPending || !entryNode) return;
    // Wait for the wallet too, so the slot opens on the server balance
    // (with the day-1 bonus in it) rather than the local placeholder.
    if ((!session || !walletReady) && !entryWaitOver) return;
    setEntryPending(false);
    if (firstVisit) {
      handleFirstVisitGrants();
      setFirstSlotSession(true);
      setShowSpinHint(true);
    }
    // Back from the slot lands on this world's map.
    setCurrentBiomeIdx(entryNode.biome);
    setView('map');
    handlePlayWorldNode(entryNode);
    // Runs once, when the session (or the wait) resolves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryPending, entryNode, session, walletReady, entryWaitOver]);
  // The hint is drawn on the slot's canvas.
  const spinHintOn = scene === 'slot' && showSpinHint && gameSceneShown;
  useEffect(() => {
    slotSceneRef.current?.setSpinHint(spinHintOn);
    if (!spinHintOn) return;
    const t = setTimeout(() => setShowSpinHint(false), SPIN_HINT_MS);
    return () => clearTimeout(t);
  }, [spinHintOn]);
  // The first-visit slot session ends when they leave the slot (not while
  // the entry splash is still opening it).
  useEffect(() => {
    if (firstSlotSession && !entryPending && !(view === 'game' && scene === 'slot')) {
      setFirstSlotSession(false);
      setShowSpinHint(false);
    }
  }, [firstSlotSession, entryPending, view, scene]);

  /** The map's crown: the multiplayer Wheel of the world (as the old world
   *  map's crown). */
  const handleMapWheel = (world: number): void => {
    setCurrentBiomeIdx(world);
    const biome = BIOMES[world];
    startWheelRoom(themeIdForBiome(world), 'player', biome ? `${biome.name} Wheel` : 'Wheel');
  };
  const handleMapGift = (): void => {
    storyBridge.dailyReleased = true;
    setShowDailyBonus(true);
  };
  /** The world whose own map a screen shows, or null for the realm map (the
   *  overview; also a world without a map, or with world_maps off). */
  const mapWorld = (sc: MapScreen): number | null =>
    worldMapsOn && sc.world !== null && worldMapOf(sc.world) ? sc.world : null;
  // The maps mounted: the one going, while the new one fades in over it,
  // then the new one alone.
  const mapLayers =
    mapUnder && mapWorld(mapUnder) !== mapWorld(mapScreen) ? [mapUnder, mapScreen] : [mapScreen];
  /** The overview glided into a world with a map: open it over the overview. */
  const openWorldMap = (world: number, nodeId: number | null): void => {
    setMapUnder(mapScreen);
    setMapUnderOut(false);
    setStripFrom(null);
    setMapScreen((prev) => ({ world, focus: nodeId, key: prev.key + 1 }));
  };

  return (
    <div className="flex h-dvh w-dvw flex-col bg-[#0a0518] text-white">
      <main className="relative flex-1 overflow-hidden">
        {/* Social MVP — "join a friend's table by code" entry. Lives on
            the biome-list + map views (not inside MapHud) as a small
            corner pill so it doesn't fight the HUD layout. Opens the
            JoinByCodeModal; the crown next to it still spins up your own
            (shareable) table. */}
        {view === 'biomeList' && !entryPending && !realmHome ? (
          <PillButton
            onClick={() => setShowJoinByCode(true)}
            icon={
              <img
                src={joinIconUrl}
                alt=""
                aria-hidden
                style={{ width: 30, height: 30, objectFit: 'contain' }}
              />
            }
            style={{ position: 'absolute', left: 14, bottom: 14, zIndex: 30 }}
          >
            Join with code
          </PillButton>
        ) : null}
        {view === 'biomeList' && !entryPending && !realmHome ? (
          <PillButton
            onClick={() => setShowFriends(true)}
            icon={
              <img
                src={friendsIconUrl}
                alt=""
                aria-hidden
                style={{ width: 30, height: 30, objectFit: 'contain' }}
              />
            }
            // Stacks above the Join-with-code pill (bottom: 14).
            style={{ position: 'absolute', left: 14, bottom: 76, zIndex: 30 }}
          >
            Friends
          </PillButton>
        ) : null}
        {/* §6.E — "Schedule a game with friends" promoted to the home screen,
            stacked above Friends (it's the appointment-play hook). Also reachable
            from Friends → Invite. */}
        {view === 'biomeList' && !entryPending && !realmHome ? (
          <PillButton
            onClick={() => setShowScheduleGame(true)}
            icon={
              <img
                src={clockIconUrl}
                alt=""
                aria-hidden
                style={{ width: 30, height: 30, objectFit: 'contain' }}
              />
            }
            // Stacks above the Friends pill (bottom: 76).
            style={{ position: 'absolute', left: 14, bottom: 138, zIndex: 30 }}
          >
            Schedule a game
          </PillButton>
        ) : null}
        {(view === 'biomeList' || view === 'map') && !entryPending && realmHome ? (
          <>
            {mapLayers.map((screen) => {
              const going = screen !== mapScreen;
              const world = mapWorld(screen);
              const mapCovered =
                going ||
                showFriends || showJoinByCode || showScheduleGame || pendingUnlockNode !== null || showDailyBonus || ageCheck !== 'ok';
              if (world === null) {
                const overviewProps = {
                  unlockedSlotIds,
                  currentNodeId: worldCurrentNodeId,
                  playerId: player?.id,
                  sessionId: session?.id ?? null,
                  coins: balance,
                  walletAnchors,
                  onWalletChanged: () => void syncWallet(),
                  onPlayNode: handlePlayWorldNode,
                  onFriends: () => setShowFriends(true),
                  onJoinWithCode: () => setShowJoinByCode(true),
                  onSchedule: () => setShowScheduleGame(true),
                  onMainGame: handleMapWheel,
                  giftReady: canClaimDaily,
                  onGift: handleMapGift,
                  alive: features.has('realm_alive'),
                  covered: mapCovered,
                  overview: worldMapsOn ? { worlds: worldsWithMaps(), onOpenWorld: openWorldMap } : undefined,
                  fadeIn: !going && mapLayers.length > 1,
                  onDrawn: () => setMapUnderOut(true),
                  onShown: () => {
                    setMapUnder(null);
                    setMapUnderOut(false);
                  },
                  onFailed: () => {
                    // The overview could not start under the map it
                    // replaces: stay on that map, still working.
                    if (mapUnder) setMapScreen(mapUnder);
                    setMapUnder(null);
                    setMapUnderOut(false);
                  },
                };
                // Before the player's flags arrive a device builds the overview it remembers; when they say the strip, the strip replaces it
                // by a cross-fade (OverviewSwap).
                return (
                  <OverviewSwap
                    key="overview"
                    stripOn={stripOn}
                    old={(fadeOut) => <RealmMap key="realm" {...overviewProps} fadeOut={fadeOut} />}
                    strip={({ swapping, drawn }) => (
                      <RealmMap
                        key="realm-strip"
                        {...overviewProps}
                        strip={{ opening: stripFrom !== null ? { world: stripFrom, zoomed: true } : undefined }}
                        fadeIn={swapping || overviewProps.fadeIn}
                        onDrawn={swapping ? drawn : overviewProps.onDrawn}
                        onShown={swapping ? undefined : overviewProps.onShown}
                        onFailed={swapping ? undefined : overviewProps.onFailed}
                      />
                    )}
                  />
                );
              }
              return (
                <WorldMap
                  key={`world-${world}-${screen.key}`}
                  world={world}
                  focusNodeId={screen.focus}
                  unlockedSlotIds={unlockedSlotIds}
                  currentNodeId={worldCurrentNodeId}
                  playerId={player?.id}
                  roadOn={(themeId) => features.onSlot('royal_road', themeId)}
                  onPlayNode={handlePlayWorldNode}
                  onBack={() => {
                    setStripFrom(world);
                    setMapUnder(mapScreen);
                    setMapUnderOut(false);
                    setMapScreen((prev) => ({ ...OVERVIEW, key: prev.key + 1 }));
                  }}
                  onFriends={() => setShowFriends(true)}
                  onJoinWithCode={() => setShowJoinByCode(true)}
                  onSchedule={() => setShowScheduleGame(true)}
                  onMainGame={handleMapWheel}
                  giftReady={canClaimDaily}
                  onGift={handleMapGift}
                  covered={mapCovered}
                  fadeIn={!going && mapLayers.length > 1}
                  leaving={going && mapUnderOut}
                  onDrawn={() => setMapUnderOut(true)}
                  onShown={() => {
                    setMapUnder(null);
                    setMapUnderOut(false);
                  }}
                  onFailed={() => {
                    // The world's map could not start: the player stays on
                    // the overview (or goes to it, if the map was the only
                    // screen), working.
                    setMapUnder(null);
                    setMapUnderOut(false);
                    if (!going) setMapScreen((prev) => ({ ...OVERVIEW, key: prev.key + 1 }));
                  }}
                />
              );
            })}
          </>
        ) : view === 'biomeList' && !entryPending ? (
          <BiomeListScreen
            unlockedSlotIds={unlockedSlotIds}
            favouriteSlotIds={favouriteSlotIds}
            onPlayFavourite={handlePlayWorldNode}
            onSelectBiome={(idx) => {
              setCurrentBiomeIdx(idx);
              setView('map');
            }}
          />
        ) : view === 'map' ? (
          useWorldMapV2 ? (
            <WorldMapV2
              currentNodeId={worldCurrentNodeId}
              completed={worldCompleted}
              currentBiomeIdx={currentBiomeIdx}
              onPlayNode={handlePlayWorldNode}
              unlockedSlotIds={unlockedSlotIds}
              slotStars={slotStars}
              onClaimDailyQuest={claimDailyQuestNow}
              coinBalance={balance}
              gemBalance={gems}
              walletLoading={!walletReady}
              streak={streak}
              level={level}
              onOpenDailyBonus={canClaimDaily ? () => {
                storyBridge.dailyReleased = true;
                setShowDailyBonus(true);
              } : null}
              onOpenMainGame={handleOpenMainGame}
              onTapGems={() => setShowGemInfo(true)}
              onBackToBiomes={() => setView('biomeList')}
              onOpenAvatarPicker={() => setShowAvatarPicker(true)}
            />
          ) : (
            <WorldMapView
              currentNodeId={worldCurrentNodeId}
              completed={worldCompleted}
              onPlayNode={handlePlayWorldNode}
              timeOfDay={worldTimeOfDay}
              onOpenAvatarPicker={() => setShowAvatarPicker(true)}
            />
          )
        ) : view === 'cast' ? (
          <WheelLobbyView
            roomId={wheelRoomId}
            playerId={wheelRoomMode === 'player' ? player?.id ?? null : null}
            playerName={player?.displayName ?? null}
            audience={wheelRoomMode === 'audience'}
            onTakeSeat={handleTakeSeat}
            errorMessage={wheelRoomError}
            roomName={wheelRoomName}
            inviteCode={wheelRoomInviteCode}
            theme={
              // Player's chosen reel-skin wins, then the room's own
              // theme, then the app default. The skin override is a
              // purely-local cosmetic — it never touches the room or
              // other players.
              (mpSkinThemeId ? registry.get(mpSkinThemeId) : null) ??
              (wheelRoomThemeId ? registry.get(wheelRoomThemeId) : null) ??
              currentTheme
            }
            availableSkins={unlockedSkins}
            selectedSkinId={
              mpSkinThemeId ?? wheelRoomThemeId ?? currentTheme.id
            }
            onSelectSkin={setMpSkinThemeId}
            profile={profile}
            onboardingSession={player?.onboardingSession ?? null}
            onOpenSettings={() => setShowSettings(true)}
            coinBalance={balance}
            gemBalance={gems}
            streak={streak}
            level={level}
            onTapGems={() => setShowGemInfo(true)}
            onPayoutLanded={() => {
              void syncWallet();
            }}
          />
        ) : (
          <>
            <CanvasHost
              scene={scene}
              theme={
                (canvasSceneThemeId ? registry.get(canvasSceneThemeId) : null) ??
                currentTheme
              }
              profile={profile}
              slotSpinFn={slotSpinFn}
              slotGambleFn={slotGambleFn}
              slotBetLadder
              // F2.2: a first visit's opening slot is onboarding session 1,
              // bet locked at 25 for a non-trivial first payout (UX Spec §4.1).
              onboardingSession={firstSlotSession ? 1 : null}
              onSlotSpinFired={showSpinHint ? () => setShowSpinHint(false) : null}
              onSlotServerSpinLanded={handleSlotSpinLanded}
              onSlotGambleOpenChange={(gamble) => setCanvasCover((c) => ({ ...c, gamble }))}
              onSlotCanvasPopupChange={(popup) => setCanvasCover((c) => ({ ...c, popup }))}
              onSlotSceneReady={(inst) => {
                slotSceneRef.current = inst;
                // Mini-games fetch their art and sounds on any slot (the
                // Sweet Trail's games play on slots without the road).
                inst?.setGameArt(loadGameArt);
                setSlotServerMode(inst?.serverMode ?? false);
                if (inst) journey.onSceneReady();
              }}
              onSlotServerModeChange={setSlotServerMode}
              onSceneShown={handleGameSceneShown}
              // T2 — natural scene completion (e.g. chest-open finished,
              // daily-quest claim animation done) routes back to the
              // biome picker. Same destination as the back-arrow
              // (handleExitToMap) so the two exit paths agree on where
              // the player lands. Producer brief 2026-05-29.
              onSceneComplete={() => setView('biomeList')}
              onOpenSettings={() => setShowSettings(true)}
              slotSettingsHost={slotSettingsHost(player?.id, features.has('road_story'))}
              walletAnchors={walletAnchors}
              // M1 slot mastery — record each spin's wager against the
              // slot the player entered, so its stars/medal fill.
              slotNodeId={worldCurrentNodeId}
              road={roadMode}
              onBoardStopPressed={() => void journey.onStopPressed()}
              onBoardCollectPressed={() => void journey.onCollectPressed()}
              onSlotIdle={() => {
                journey.onSlotIdle();
                storyBridge.onSlotIdle();
              }}
            />
            {/* The story stays on the eight roads: none on a Wheel Run slot. */}
            {scene === 'slot' && roadMode && ROAD_THEMES.includes(slotThemeId ?? '') && features.has('road_story') ? (
              <SlotStory
                enabled
                sessionId={session?.id ?? null}
                playerId={player?.id ?? null}
                profile={profile}
                getScene={() => slotSceneRef.current}
                themeId={slotThemeId}
                slotNameOf={(id) => registry.get(id)?.name}
                road={journey.road}
                run={journey.view}
                words={{
                  collectorNoun: trailThemeInfo(registry.get(slotThemeId ?? '') ?? null).collectorNoun,
                  slotName: registry.get(slotThemeId ?? '')?.name ?? 'the woods',
                  runName: RUN_NAME,
                }}
                art={storyArtSources(registry.get(slotThemeId ?? '') ?? null)}
                syncWallet={syncWallet}
                openGame={journey.openGame}
              />
            ) : null}
            {scene === 'slot' && roadMode ? (
              <SlotJourneyChrome
                road={journey.road}
                scene={slotSceneRef.current}
                spinKey={journey.spinKey}
                safeTop={safeAreaTop()}
                hidden={!gameSceneShown || !slotServerMode || showSpinHint || gambleOpen}
                view={journey.view}
                notice={journey.notice}
                bonus={journey.bonus}
                sessionId={session?.id ?? null}
                playerId={player?.id ?? null}
                onBonusDone={(payout) => {
                  journey.clearBonus();
                  if (payout) void syncWallet();
                }}
                themeId={slotThemeId}
                unlockedSlotIds={unlockedSlotIds}
                roadOn={roadOn}
                onRestore={journey.restore}
                // Finishing a chapter opens the next slot for free.
                onSlotOpened={(id) => profile.unlockSlot(id, 0)}
                onPlaySlot={(id) => {
                  const node = WORLD_NODES.find((n) => String(n.id) === id);
                  if (node) handlePlayWorldNode(node);
                }}
              />
            ) : null}
            {/* Relics on every slot: the server says which have them. */}
            {scene === 'slot' && slotThemeId ? (
              <SlotRelics scene={slotSceneRef.current} sessionId={session?.id ?? null} themeId={slotThemeId} />
            ) : null}
            {spinHintOn ? <FirstSpinHint /> : null}
            {scene === 'slot' && !roadMode ? (
              <SlotTrail
                scene={slotSceneRef.current}
                trail={trail}
                spinKey={trailSpinKey}
                revealDelayMs={trailRevealDelay}
                hidden={!gameSceneShown || !slotServerMode || showSpinHint || gambleOpen}
                collectorNoun={trailThemeInfo(registry.get(trail?.themeId ?? slotThemeId ?? '') ?? null).collectorNoun}
                bonusShown={showTrailBonus}
                onOpenBonus={() => setShowTrailBonus(true)}
                onPick={handleTrailPick}
                onBonusClosed={(after) => {
                  setShowTrailBonus(false);
                  // A finished bonus: drop the reveal so the panel shows the new level.
                  if (after) setTrail(after.bonus?.finished ? { ...after, bonus: null } : after);
                  // The bonus held the slot's road back: played, the road starts.
                  if (after && !sweetTrailBonusWaits(after)) setTrailFirst(null);
                }}
                // The bonus can be a mini-game instead of the boxes (trail_games).
                sessionId={session?.id ?? null}
                playerId={player?.id ?? null}
                profile={profile}
              />
            ) : null}
            {scene === 'slot' && gameSceneShown && !gambleOpen && !canvasPopup && !journey.runActive ? (
              <PillButton
                onClick={handlePlayWithOthers}
                icon={
                  <img
                    src={friendsIconUrl}
                    alt=""
                    aria-hidden
                    style={{ width: 30, height: 30, objectFit: 'contain' }}
                  />
                }
                style={{
                  position: 'absolute',
                  right: 'calc(env(safe-area-inset-right, 0px) + 14px)',
                  bottom: 'calc(env(safe-area-inset-bottom, 0px) + 18px)',
                  zIndex: 45,
                  // Bright pink like SPIN so it reads as an action, not HUD chrome.
                  background:
                    'linear-gradient(180deg, #FF6FC0 0%, #E3268E 100%)',
                  border: '2px solid #FFE48A',
                  fontSize: 14,
                  padding: '6px 16px 6px 8px',
                  boxShadow:
                    '0 4px 14px rgba(0,0,0,0.45), 0 0 18px rgba(255,63,164,0.55)',
                }}
              >
                Play with others
              </PillButton>
            ) : null}
          </>
        )}
        {/* Slot/legacy SSE status badge — engineer telemetry ("N
            ticks · N listening") that should never reach players.
            Gate to dev builds AND only the slot scene AND an explicit
            opt-in (localStorage.goodtime.devBadge === 'on'). Producer
            audit found the badge polluting screenshots even in dev,
            and the visible "ticks" counter was misleading playtesters
            into thinking the game was unstable. Devs who want it can
            toggle on via the localStorage flag. */}
        {view === 'game' &&
        scene === 'slot' &&
        import.meta.env.DEV &&
        typeof window !== 'undefined' &&
        window.localStorage.getItem('goodtime.devBadge') === 'on' ? (
          <StatusBadge
            status={status}
            tickCount={tickCount}
            subscribers={subscribers}
            errorMessage={errorMessage}
          />
        ) : null}
        {/* Persistent wallet HUD — top-right overlay across every
            scene that isn't the map (the map ships its own copy
            inside MapHud's right-cluster). Per the slot-HUD redesign
            brief: "balance hud from the map is top right — this
            should always be present for the player."
            <p>The wheel view ('cast') mounts its OWN copy of the
            BalancePill inside {@link WheelLobbyView} so the pill can
            grow a bonus-pot + item cluster from the live SSE state.
            Skip the global mount there to avoid duplicate pills. */}
        {view !== 'map' && view !== 'cast' ? (
          // The row starts right of the cog / back button at the top left
          // and tells the pill its room, so on a narrow phone the pill
          // tightens rather than running under the button.
          <WalletRow>
            {(maxWidth) => (
              <BalancePill
                coinBalance={balance}
                gemBalance={gems}
                streak={streak}
                showStreak={!(view === 'game' && scene === 'slot')}
                level={level}
                onTapGems={() => {
                  // Over the slot, the slot shows its own wallet board.
                  const slot = view === 'game' && scene === 'slot' ? slotSceneRef.current : null;
                  if (slot) void slot.showWallet(gems, balance, features.has('reel_gems'));
                  else setShowGemInfo(true);
                }}
                loading={!walletReady}
                onWalletAnchors={setWalletAnchors}
                maxWidth={maxWidth}
              />
            )}
          </WalletRow>
        ) : null}
        {/* The map button (the way back) over the wheel cast lobby and
            the single-player canvas. Hidden on the map (it has its own
            back button) and on the biomeList itself (it's the home;
            nothing to go back to). */}
        {view !== 'map' && view !== 'biomeList' ? (
          <BackToMapButton
            onExit={() => void storyBridge.beforeExit().then((leave) => leave && handleExitToMap())}
            info={mapInfo}
            disabled={journey.runActive}
          />
        ) : null}
        {/* Settings cog — the biome list (home) has no in-canvas gear, so
            mount the same SettingsModal entry here (producer 2026-06-07).
            Top-left, sized to match the wallet pill + slot back button. */}
        {view === 'biomeList' && !entryPending ? (
          <PillButton
            onClick={() => setShowSettings(true)}
            ariaLabel="Settings"
            icon={<Icon name="settings" size={22} alt="" />}
            style={{
              position: 'absolute',
              top: 'calc(env(safe-area-inset-top, 0px) + 4px)',
              left: 'calc(env(safe-area-inset-left, 0px) + 14px)',
              zIndex: 46,
              width: 44,
              height: 44,
              padding: 0,
            }}
          />
        ) : null}
        {entryPending ? <EntrySplash /> : null}
        {ageCheck !== 'ok' ? (
          <AgeGate
            state={ageCheck}
            onShown={() => funnelBeacon('age_gate', { playerId: playerRef.current?.id, detail: 'shown' })}
            onResult={(r) => {
              funnelBeacon('age_gate', { playerId: playerRef.current?.id, detail: r === 'ok' ? 'pass' : 'under' });
              setAgeCheck(r);
            }}
          />
        ) : null}
        {/* Off the slot, settings, the wallet and the daily bonus are the
            slot's own boards, on the canvas over the screen. */}
        {showSettings ? (
          <OverlaySettings
            profile={profile}
            host={{
              ...slotSettingsHost(player?.id, features.has('road_story')),
              // At a wheel table, the reel skin to play on.
              skins:
                wheelRoomId !== null
                  ? {
                      list: () => unlockedSkins,
                      selected: () => mpSkinThemeId ?? wheelRoomThemeId ?? currentTheme.id,
                      select: setMpSkinThemeId,
                    }
                  : null,
            }}
            onClose={() => setShowSettings(false)}
          />
        ) : showGemInfo ? (
          <OverlayWallet gems={gems} coins={balance} reelGems={features.has('reel_gems')} onClose={() => setShowGemInfo(false)} />
        ) : showDailyBonus && walletReady && (!features.has('road_story') || storyBridge.dailyReleased) ? (
          // After the wallet sync, so CLAIM goes to the server wallet (or,
          // with no connection, the local profile) and never to a local
          // profile the sync then overwrites.
          slotThemeId !== null && slotSceneRef.current ? (
            // Over the slot, the slot shows it on its own canvas.
            <SlotDailyBonus
              scene={slotSceneRef.current}
              profile={profile}
              onClaim={player?.id ? claimDailyBonusNow : undefined}
              onDone={() => setShowDailyBonus(false)}
            />
          ) : (
            <OverlayDailyBonus
              profile={profile}
              onClaim={player?.id ? claimDailyBonusNow : undefined}
              target={() => walletAnchors?.coin ?? null}
              onDone={() => setShowDailyBonus(false)}
            />
          )
        ) : showAvatarPicker ? (
          <AvatarPickerModal
            playerId={player?.id ?? null}
            onClose={() => setShowAvatarPicker(false)}
          />
        ) : showJoinByCode ? (
          <JoinByCodeModal
            onClose={() => setShowJoinByCode(false)}
            onJoin={async (code) => {
              await joinWheelRoomByCode(code);
              setShowJoinByCode(false);
            }}
          />
        ) : showFriends ? (
          <FriendsModal
            playerId={player?.id}
            onInvite={handleInviteFriend}
            onClose={() => setShowFriends(false)}
          />
        ) : null}
        {/* T1 — slot purchase sheet. Sits above the world map at z-50
            (matching the shop modal layer). The handler routes the
            player to the scene on success so the unlock-then-play
            flow is a single tap. */}
        {pendingUnlockNode !== null ? (
          <SlotUnlockSheet
            node={pendingUnlockNode}
            profile={profile}
            offer={unlockOffer}
            reelGems={features.has('reel_gems')}
            theme={registry.get(themeIdForNode(pendingUnlockNode)) ?? null}
            onUnlocked={(node) => {
              setPendingUnlockNode(null);
              setUnlockOffer(false);
              funnelBeacon('slot_unlocked', { playerId: playerRef.current?.id, detail: String(node.id) });
              if (unlockOfferRef.current) funnelBeacon('slot_offer', { playerId: playerRef.current?.id, detail: `open:${node.id}` });
              unlockOfferRef.current = false;
              routeWorldNode(node);
            }}
            onClose={() => {
              if (unlockOfferRef.current) funnelBeacon('slot_offer', { playerId: playerRef.current?.id, detail: `later:${pendingUnlockNode.id}` });
              unlockOfferRef.current = false;
              setPendingUnlockNode(null);
              setUnlockOffer(false);
            }}
          />
        ) : null}
        {levelUpFlash !== null ? (
          <div className="pointer-events-none absolute left-1/2 top-4 z-40 -translate-x-1/2 rounded-lg border-2 border-amber-300/90 bg-gradient-to-b from-amber-800 to-amber-950 px-5 py-2 text-sm font-black tracking-widest text-amber-100 shadow-lg shadow-black/50">
            LEVEL {levelUpFlash}!
          </div>
        ) : null}
        {/* The free refill: drawn on the canvas over every screen, so the
            brand promise lands even if the player busts mid-spin or
            mid-wheel-match. The coins fly into the coin counter. */}
        <RefillBurst
          active={refillBurst !== null}
          fromBalance={refillBurst?.from ?? 0}
          amount={refillBurst?.amount ?? 1000}
          target={() => walletAnchors?.coin ?? null}
          onComplete={() => setRefillBurst(null)}
        />
        {/* T1 polish (2026-05-29) — floating "+N 💎" toast that fires
            whenever {@code profile.gems} increases. Anchors top-right
            near the wallet pill so the player's eye already lives in
            the right place when the credit lands. Mounted at the App
            level so it works across every view (biomeList / map /
            game / cast). */}
        <GemDropToast profile={profile} />
        {/* Invite-friends dialogue — opened from the Friends modal's
            "Invite a friend" CTA. Game-level personal referral: share a
            link to the GAME (not a single table); the friend earns coins on
            join, the player once the friend has played. Portals to body so it floats above the canvas + HUD. */}
        {showInviteFriends ? (
          <InviteFriendsDialog
            playerId={player?.id}
            onScheduleGame={() => {
              setShowInviteFriends(false);
              setShowScheduleGame(true);
            }}
            onClose={() => setShowInviteFriends(false)}
          />
        ) : null}
        {/* §6.E — "Schedule a game with friends" host modal. Opened from the
            invite-friends dialogue. Picks a time + invitees, returns a
            shareable ?game= link + add-to-calendar; the host's upcoming list
            lives inside it too. */}
        {showScheduleGame ? (
          <ScheduleGameModal
            playerId={player?.id}
            currentThemeId={currentTheme.id}
            availableThemes={unlockedSkins}
            onClose={() => setShowScheduleGame(false)}
          />
        ) : null}
        {/* §6.E — an invited friend's scheduled-game card, from a ?game=CODE
            launch link. The first thing a friend sees: host / theme / time /
            add-to-calendar, with a state-aware Join (race-safe room claim).
            Reuses the existing join-by-code path. */}
        {scheduledGameInvite ? (
          <ScheduledGameCard
            game={scheduledGameInvite}
            playerId={player?.id}
            onJoinByRoomCode={joinWheelRoomByCode}
            onClose={() => {
              setScheduledGameInvite(null);
              clearPendingGameCode();
            }}
          />
        ) : null}
        <AccountLayer
          playerId={player?.id ?? null}
          enabled={features.has('accounts')}
          ready={ageCheck === 'ok' && !entryPending}
          firstVisit={firstVisit}
          canInterrupt={() =>
            !showSettingsRef.current &&
            !journeyBonusRef.current &&
            !offerBlockedRef.current &&
            !journey.runActive &&
            (slotSceneRef.current?.idle ?? true) &&
            !slotSceneRef.current?.boardOpen
          }
        />
        {/* U12 M2/M4 (2026-05-31) — surfaces server-sync failures
            from {@link useSlotUnlockSync} + {@link useWalletSync} as
            a top-of-screen toast. Player keeps their local action;
            the toast just confirms the round-trip didn't land so
            they're not surprised on the next session if the server
            hasn't caught up yet. */}
        <SyncErrorBanner profile={profile} />
      </main>
    </div>
  );
}

/** A Sweet Trail bonus waits on the trail: its boxes, or its game. */
function sweetTrailBonusWaits(trail: SlotTrailState): boolean {
  return (trail.bonus !== null && !trail.bonus.finished) || !!trail.game;
}

/**
 * What the slot's own settings board can't do itself: push notifications
 * (with a player) and, in the iOS or Android app, restoring purchases; and
 * whether it shows the story's section.
 */
function slotSettingsHost(playerId: string | undefined, story: boolean): SlotSettingsHost {
  if (!playerId) return { story: () => story };
  return {
    story: () => story,
    push: {
      enabled: isPushEnabled,
      set: async (on) => {
        if (on) return enablePushNotifications(playerId);
        await disablePushNotifications();
        return false;
      },
    },
    restore: Capacitor.isNativePlatform() ? () => restorePurchases(playerId).then(() => undefined) : null,
    // The optional account's row, while accounts are on (lib/accountEvents).
    account: {
      status: () => {
        const status = accountStatusSnapshot();
        return status?.available ? { email: status.account?.email ?? null } : null;
      },
      open: openAccountBoard,
    },
  };
}

/** The map on screen: the realm map (world null) or a world's own map,
 *  centred on `focus` as it opens. `key` tells one opening from the next. */
interface MapScreen {
  world: number | null;
  focus: number | null;
  key: number;
}

const OVERVIEW: MapScreen = { world: null, focus: null, key: 0 };

/** First-visit nudge over the opening slot: what to tap, and the free-coins
 *  promise. The slot draws it on its canvas; this says it to screen readers
 *  (and is what the browser tests wait for). Doesn't take taps; goes away
 *  on the first spin or after {@link SPIN_HINT_MS}. */
function FirstSpinHint() {
  return (
    <div
      role="status"
      style={{
        position: 'absolute',
        width: 1,
        height: 1,
        margin: -1,
        padding: 0,
        overflow: 'hidden',
        clip: 'rect(0 0 0 0)',
        whiteSpace: 'nowrap',
        border: 0,
        pointerEvents: 'none',
      }}
    >
      <div>{SPIN_HINT_TITLE}</div>
      <div>{SPIN_HINT_LINE}</div>
    </div>
  );
}

/** Covers the home screen for the moment between boot and the entry slot
 *  opening on a first visit (at most {@link ENTRY_SESSION_WAIT_MS}). */
function EntrySplash() {
  return (
    <div
      aria-label="Loading"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
        background: 'radial-gradient(ellipse at 50% 35%, #3a1d6e 0%, #140826 70%)',
        color: '#ffd666',
        fontFamily: 'Fredoka, -apple-system, system-ui',
      }}
    >
      <style>{`
        @keyframes entry-splash-dot {
          0%, 80%, 100% { opacity: 0.25; }
          40% { opacity: 1; }
        }
      `}</style>
      <div style={{ fontSize: 30, fontWeight: 800, letterSpacing: 1 }}>Goodtime</div>
      <div style={{ display: 'flex', gap: 8 }}>
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            style={{
              width: 9,
              height: 9,
              borderRadius: '50%',
              background: '#ffd666',
              animation: `entry-splash-dot 1s ${i * 0.15}s infinite`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

function StatusBadge({
  status,
  tickCount,
  subscribers,
  errorMessage,
}: {
  status: ConnectionStatus;
  tickCount: number;
  subscribers: number;
  errorMessage: string | null;
}) {
  const text =
    status === 'error'
      ? `⚠️ ${errorMessage ?? 'connection error'}`
      : status === 'connecting'
        ? 'connecting to server…'
        : status === 'disconnected'
          ? 'reconnecting…'
          : `${tickCount} ticks · ${subscribers} listening`;

  const tone =
    status === 'error'
      ? 'bg-red-500/20 text-red-200'
      : status === 'live'
        ? 'bg-emerald-500/20 text-emerald-100'
        : 'bg-white/10 text-white/60';

  return (
    <div
      className={`absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full px-4 py-2 text-xs font-medium ${tone}`}
    >
      {text}
    </div>
  );
}
